/* eslint-disable no-undef */
const turfCircle = require('@turf/circle').default
const turfIntersect = require('@turf/intersect').default
const turfLineSlice = require('@turf/line-slice').default
const turfLineSliceAlong = require('@turf/line-slice-along').default
const turfLength = require('@turf/length').default
const turfHelpers = require('@turf/helpers')
const turfNearestPointOnLine = require('@turf/nearest-point-on-line').default
const turfDistance = require('@turf/distance').default
const turfPointsWithinPolygon = require('@turf/points-within-polygon').default
const turfPointToLineDistance = require('@turf/point-to-line-distance').default
const turfBboxPolygon = require('@turf/bbox-polygon').default
const polyline = require('@mapbox/polyline')
const _ = require('lodash')
const stableStringify = require('./vendors/stable-stringify')

/**
 * Checks if the circle created by two lngLat objects intersect
 * @param {object} lngLat1 - {lng, lat, distance <optional>}
 * @param {number} [lngLat1.lng] - longitude
 * @param {number} [lngLat1.lat] - latitude
 * @param {number} [lngLat1.distance] - size of radius to cast when checking for intersect
 * @param {object} lngLat2 - {lng, lat, distance <optional>}
 * @param {number} [lngLat2.lng] - longitude
 * @param {number} [lngLat2.lat] - latitude
 * @param {number} [lngLat2.distance] - size of radius to cast when checking for intersect
 * @param {string} units='kilometers' - can be degrees, radians, miles, kilometers, inches, yards, meters
 * @returns {boolean}
 */
function _isWithinRadius (lngLat1, lngLat2, units) {
  const parsedUnit = units || 'kilometers'
  if (!lngLat1 || !lngLat2 || !Object.keys(lngLat1).length || !Object.keys(lngLat1).length) {
    throw new Error('Pair of Longitude and Latitude objects required: {lng, lat}')
  }
  const parsedDistance1 = lngLat1.distance || 0.05
  const parsedDistance2 = lngLat2.distance || 0.05
  const clickedCircle1 = turfCircle([lngLat1.lng, lngLat1.lat], parsedDistance1, { units: parsedUnit })
  const clickedCircle2 = turfCircle([lngLat2.lng, lngLat2.lat], parsedDistance2, { units: parsedUnit })

  return !!turfIntersect(clickedCircle1, clickedCircle2)
}

/**
 * Convert google polyline string to MapBox compatible GeoJSON
 * @param {string} polylineData
 * @param {number} precision
 * @returns {{type, coordinates}}
 */
function _convertPolylineToGeoJSON (polylineData, precision) {
  return polyline.toGeoJSON(polylineData, precision)
}

/**
 * Convert google polyline string to 2 dimensional array of lon, lat coordinates
 * We will actually force it to be lon, lat because the resultant polyline is EXPECTED to return lat,lon
 * @param {string} polylineData
 * @param {number} precision
 * @returns {array} - as lon, lat pairs, NOTE mapbox expects arrays in lat, lon order
 */
// eslint-disable-next-line no-unused-vars
function _convertPolylineToCoordinates (polylineData, precision) {
  const latLon = polyline.decode(polylineData, precision)
  // convert to lon, lat for turf use
  return latLon.map(o => ([o[1], o[0]]))
}

/**
 * Convert 2 dimensional array of lon, lat coordinates to google polyline
 * IMPORTANT: We are flipping the coordinates here, bc/ polyline used by OTP have their resultant coordinates in lat,lon.
 * We will mimic this convention at least so that converting back to coordinates later will occur in the same behavior.
 * Let's just make it transparent to the developer
 * @see _convertPolylineToCoordinates - for equivalent flipping around of lat,lon back to lon,lat
 * @param {array} coordinates - as lon,lat array pairs
 * @param {number} [precision]
 * @returns {string} - as polyline string
 */
function _convertCoordinatesToPolyline (coordinates, precision) {
  coordinates = coordinates.map(o => ([o[1], o[0]]))
  return polyline.encode(coordinates, precision)
}

/**
 * OTP server returns route data sometimes as array of itinerary objects, each with the calculated distance on each 'leg' object.
 * In order to determine the total distance of the entire route, this utility function can be used to determine the total distance of all 'leg' objects in each itinerary
 * @param {array} itineraries
 * @returns {array}
 */
function _calculateTotalItineraryDistances (itineraries) {
  let itinerariesParsed = _.cloneDeep(itineraries)
  itinerariesParsed.forEach((itinerary) => {
    // itinerary.totalDistance = _.sumBy(itinerary.legs, 'distance')
    itinerary.distance = _.sumBy(itinerary.legs, 'distance')
  })

  return itinerariesParsed
}

/**
 * A convenience to filter out bad GPS data
 * Usage:
 * const filterGeolocation = new FilterGeolocation(({ longitude, latitude }) => {}, {options})
 * filterGeolocation.setGeolocation({ longitude, latitude })
 * @param {function} callback
 * @param {number} maxSampleSet=4
 * @param {number} kmPerSecond=0.023
 * @private
 */
function _FilterGeolocation (callback, { maxSampleSet = 4, kmPerSecond = 0.023 } = {}) {
  this.maxSampleSet = maxSampleSet
  this.kmPerSecond = kmPerSecond
  this.lastCoordinates = []

  this.setGeolocation = (longitude, latitude) => {
    try {
      let distance = 0

      if (this.lastCoordinates.length) {
        const previousCoordinate = this.lastCoordinates[this.lastCoordinates.length - 1]
        const from = turfHelpers.point([previousCoordinate.coordinates[0], previousCoordinate.coordinates[1]])
        const to = turfHelpers.point([longitude, latitude])
        // this is only an "as the crow flies" check
        distance = turfDistance(from, to, { units: 'kilometers' })
        const timestampDiffSec = (Date.now() - previousCoordinate.timestamp) / 1000
        // km / per second
        const maxDistance = this.kmPerSecond * timestampDiffSec
        const jumpedUnnaturally = distance > maxDistance

        if (jumpedUnnaturally && this.lastCoordinates.length < this.maxSampleSet) {
          // let's stay were we were last
          this.lastCoordinates.push({ ...this.lastCoordinates[this.lastCoordinates.length - 1], timestamp: Date.now(), unnaturalCoords: [longitude, latitude] })
        } else {
          this.lastCoordinates.push({ timestamp: Date.now(), distance, coordinates: [longitude, latitude] })
        }

        // keep the sample set within the max size
        if (this.lastCoordinates.length > this.maxSampleSet) {
          this.lastCoordinates.shift()
        }
      } else {
        this.lastCoordinates.push({ timestamp: Date.now(), distance: 0, coordinates: [longitude, latitude] })
      }

      const lastCoord = this.lastCoordinates[this.lastCoordinates.length - 1]
      const totalDistance = this.lastCoordinates.reduce((acc, curr) => acc + curr.distance, 0)
      const speedPerHour = this.lastCoordinates.length > 1 ? _.round(totalDistance / ((lastCoord.timestamp - this.lastCoordinates[0].timestamp) / 1000 / 60 / 60), 3) : 0
      callback(null, { unnaturalCoords: lastCoord.unnaturalCoords, speedPerHour, isMoving: distance > 0.01, longitude: lastCoord.coordinates[0], latitude: lastCoord.coordinates[1] })
    } catch (err) {
      callback(err)
      throw err
    }
  }
}

/**
 * Usage:
 * const routeLineChecker = new RouteLineChecker(<lineString>)
 * routeLineChecker.getClosestRouteLineWithinRadius(<coordinates>)
 * @param {*} routeLineCoordinates {object|array}
 * @param {number} radiusDistance=0.2
 * @param {string} units='kilometers' - can be degrees, radians, miles, kilometers, inches, yards, meters
 */
function _RouteLinePointChecker (routeLineCoordinates, radiusDistance, units) {
  this.parsedRadiusDistance = radiusDistance || 0.2
  this.parsedUnits = units || 'kilometers'
  this.previousPoint = null
  this.currentPoint = null
  this.lineString = null
  
  this.setRoute = (coordinates) => {
    if (!_.isArray(coordinates) && _.isObject(coordinates) && coordinates.geometry) {
      this.lineString = coordinates
    } else {
      const parsedRouteLineCoordinates = coordinates.map(o => {
        return _.isArray(o) ? o : [o.lng, o.lat]
      })
      this.lineString = turfHelpers.lineString(parsedRouteLineCoordinates)
    }
  }
  
  /**
   * Call method to obtain closest point
   * @param {*} currentCoordinates - {object|array} array of lng/lats or object or {lng, lat} or Feature Point
   * @returns {array}
   */
  this.getClosestRouteLineWithinRadius = (currentCoordinates) => {
    this.currentPoint = _normalizeCoordinateToPoint(currentCoordinates)

    if (!_isOnRoute(this.lineString, this.currentPoint)) {
      const snapped = turfNearestPointOnLine(this.lineString, this.currentPoint, { units: this.parsedUnits })
      
      const backedUpPreviousPoint = this.previousPoint
      this.previousPoint = snapped
      
      if (snapped.properties.dist <= this.parsedRadiusDistance) {
        if (_.get(backedUpPreviousPoint, 'properties.index', 0) > snapped.properties.index) {
          return backedUpPreviousPoint.geometry.coordinates
        } else {
          return snapped.geometry.coordinates
        }
      }
    } else {
      this.previousPoint = this.currentPoint
    }
    return this.currentPoint.geometry.coordinates
  }
  
  this.setRoute(routeLineCoordinates)
}

function _metersToMiles (meter) {
  return meter * 0.00062137
}

function _milesToMeters (mile) {
  return mile / 0.00062137
}

function _getRatePerSecond (distance, timeLengthInMilliseconds) {
  return distance / Math.round(timeLengthInMilliseconds / 1000)
}

/**
 * utility function to determine "best guess" of passenger on a transport. The human limitation of 7meters/second sprint speed is taken into account to assume the passenger is on transportation if their location is considered within the radius at that speed
 * @param {array} sampleSet
 * @param {object} lastSample=null - If available used to determine distance between the last coordinate set and the first one in this sample set
 * @param {number} withinRadius
 * @param {string} radiusUnits
 * @returns {boolean}
 */
function _guessIfOnTransportation (sampleSet, lastSample, withinRadius, radiusUnits) {
  let guessTrueCount = 0
  let guessFalseCount = 0
  let guessMaybeCount = 0
  let previousSample = lastSample
  const maxHumanMetersPerSecond = 7
  for (let i = 0; i < sampleSet.length; i++) {
    if (i > 0) {
      previousSample = sampleSet[i - 1]
    }
    const currentSample = sampleSet[i]
    let asTheCrowFliesDistance = 0
    if (previousSample && currentSample) {
      asTheCrowFliesDistance = turfDistance(previousSample.transportationCoordinates, currentSample.transportationCoordinates, { units: 'meters' })
    }
    const withinRadiusParams1 = {
      lng: sampleSet[i].passengerCoordinates[0],
      lat: sampleSet[i].passengerCoordinates[1],
      distance: withinRadius
    }
    const withinRadiusParams2 = {
      lng: sampleSet[i].transportationCoordinates[0],
      lat: sampleSet[i].transportationCoordinates[1],
      distance: withinRadius
    }
    const isWithinRadius = _isWithinRadius(withinRadiusParams1, withinRadiusParams2, radiusUnits)
    // console.log('isWithinRadius', isWithinRadius, 'asTheCrowFliesDistance', asTheCrowFliesDistance)
    if (isWithinRadius && asTheCrowFliesDistance) {
      const timeLength = currentSample.currentTimestamp - previousSample.currentTimestamp
      const ratePerSecond = _getRatePerSecond(asTheCrowFliesDistance, timeLength)
      if (ratePerSecond > maxHumanMetersPerSecond) {
        guessTrueCount++
      } else {
        guessMaybeCount++
      }
    } else if (isWithinRadius) {
      guessMaybeCount++
    } else {
      guessFalseCount++
    }
  }
  const max = Math.max(guessTrueCount, guessMaybeCount, guessFalseCount)
  // console.log('guessTrueCount', guessTrueCount, 'guessMaybeCount', guessMaybeCount, 'guessFalseCount', guessFalseCount, 'max', max)
  // return results TRUE if "guess true" count is the max, or "maybe" is the max AND it's over 50% of the sample set
  return max === guessTrueCount || (max === guessMaybeCount && (max / sampleSet.length) >= 0.5)
}

const ANIMATE_DISTANCE = 0.006

/**
 *
 * @param {*} startCoordinate - {array|object} if object, then {lng, lat}
 * @param {*} endCoordinate - {array|object} if object, then {lng, lat}
 * @param {object} animateHandleObj - the object: { handle } to store the animation frame handle (to enable cancelling at any time)
 * @param {*} animateHandleObj.handle - the property to set the animation frame handle
 * @param {function} callback - call this function to set the marker on your mp or do some other functionality
 * @param {number} isPositiveDirection - provides either: false = heading opposite direction, null = unknown or true = heading towards destination
 * @param {boolean} isOnRoute - provides true/false
 * @param {object} lineString=undefined - this is for the recursive call, do not use
 * @param {boolean} isMoving=undefined
 * @param {number} speedPerHour=undefined
 * @param {number} currentLineDistance=undefined - this is for the recursive call, do not use
 * @private
 */
function _animateOnLine (startCoordinate, endCoordinate, animateHandleObj, callback, isPositiveDirection, isOnRoute, lineString, isMoving, speedPerHour, currentLineDistance) {
  currentLineDistance = currentLineDistance || ANIMATE_DISTANCE

  if (!lineString) {
    const parsedStartCoordinate = _.isArray(startCoordinate) ? startCoordinate : [startCoordinate.lng, startCoordinate.lat]
    const parsedEndCoordinate = _.isArray(endCoordinate) ? endCoordinate : [endCoordinate.lng, endCoordinate.lat]
    lineString = turfHelpers.lineString([parsedStartCoordinate, parsedEndCoordinate])
  }

  const lineLength = turfLength(lineString)

  if (currentLineDistance <= lineLength) {
    const slicedLine = turfLineSliceAlong(lineString, 0, currentLineDistance)

    callback(slicedLine.geometry.coordinates[slicedLine.geometry.coordinates.length - 1], {
      isPositiveDirection,
      isInAnimation: (currentLineDistance + ANIMATE_DISTANCE < lineLength),
      isOnRoute,
      isMoving,
      speedPerHour
    })

    if (currentLineDistance + ANIMATE_DISTANCE < lineLength) {
      animateHandleObj.handle = requestAnimationFrame(() => {
        if (animateHandleObj.handle) {
          _animateOnLine(startCoordinate, endCoordinate, animateHandleObj, callback, isPositiveDirection, isOnRoute, lineString, isMoving, speedPerHour, currentLineDistance + ANIMATE_DISTANCE)
        }
      })
    }
  }
}

/**
 * Determine if a Point is on a route line (line string)
 * @param {object} lineString - GeoJSON or Feature object
 * @param {object|array} currentCoordinate - GeoJSON or Point object
 * @param {number} distance=0.001 - allowable distance from route line to be determined as "true"; always provide a number greater than '0'
 * @param {string} units='kilometers' - unit for distance provided
 * @returns {boolean}
 * @private
 */
function _isOnRoute (lineString, currentCoordinate, distance, units) {
  distance = distance || 0.001
  units = units || 'kilometers'
  let parsedCurrentCoordinate = currentCoordinate
  if (_.isArray(currentCoordinate)) {
    parsedCurrentCoordinate = turfHelpers.point(currentCoordinate)
  } else if (_.isObject(currentCoordinate)) {
    if (currentCoordinate.lat) {
      parsedCurrentCoordinate = turfHelpers.point([currentCoordinate.lng, currentCoordinate.lat])
    }
  }
  return turfPointToLineDistance(parsedCurrentCoordinate, lineString, { units }) <= distance
}

/**
 * Utility function to convert array or custom object into turf point or just return turf point if detected (by duck type detection)
 * @param {number[]|object} coordinate
 * @param {object} [options={}]
 * @param {string} [customLongProp='lng'] - name of the longitude property in the custom object
 * @param {string} [customLatProp='lat'] - name of the latitude property in the custom object
 * @returns {turfHelpers.point}
 * @private
 */
function _normalizeCoordinateToPoint (coordinate, { customLongProp = 'lng', customLatProp = 'lat' } = {}) {
  let currentPoint

  if (!_.isArray(coordinate) && _.isObject(coordinate) && coordinate.geometry) {
    currentPoint = coordinate
  } else {
    const parsedCurrentCoordinates = _.isArray(coordinate) ? coordinate : [coordinate[customLongProp], coordinate[customLatProp]]
    currentPoint = turfHelpers.point(parsedCurrentCoordinates)
  }

  return currentPoint
}

/**
 * Create an instance to use and determine movement on map with "fake" movement inbetween GPS coordinates
 * Usage:
 * const mover = new maputilities.RouteLineMover((mapCoordinate, isPositiveDirection) => {
 *   // this function is the callback to set the coordinate on the map of your marker
 *   busMarker.setLngLat(mapCoordinate)
 * })
 * mover.setRoute(<array of route coordinates or LineString Feature object>)
 * // in you GPS watcher call this function:
 * mover.setCurrentCoordinate(<array of lng, lat coordinates>)
 * @param {function} callback - callback(lngLatArray, {isPositiveDirection, isInAnimation, isOnRoute, isMoving}): lnglatArray are the coordinates to set the marker, isPositiveDirection provides either: false = heading opposite direction, null = unknown or true = heading towards destination
 * @param {object} [options={}]
 * @param {boolean} [options.reduceResources=false] - set to true to reduce CPU usage in slower environments (avoids animations and optimistic movements)
 * @param {boolean} [options.noOptimisticMovement=false] - perform the "fake" movements inbetween GPS coordinate updates
 * @param {number} [options.maxDistanceToSnapToRouteline=0.2] - distance in kilometers to snap to the route line, if distance exceeds this, don't snap
 * @param {boolean} [options.debug=false] - console log activities
 * @param {number} [options.minDirectionChangeDistance=0.05] - minimum distance change (km) to consider for direction (distance-based mode)
 * @param {number} [options.baseDirectionConfirmationCount=3] - confirmations needed near route start (fast turnarounds)
 * @param {number} [options.midRouteDirectionConfirmationCount=8] - confirmations needed in middle of route (conservative)
 * @param {number} [options.endRouteDirectionConfirmationCount=8] - confirmations needed near route end (conservative)
 * @param {number} [options.endProximityThreshold=0.15] - distance (km) to consider "near" route endpoints (improved from 0.05)
 * @param {number} [options.maxSnapDistance=0.5] - maximum distance (km) to allow GPS snapping
 * @param {string} [options.transportationType='default'] - transportation type for specific thresholds ('bus', 'rail', 'delivery', 'rideshare', 'highway', 'default')
 * @param {boolean} [options.useIndexBasedDirection=true] - use index-based direction detection (more performant and accurate)
 * @param {number} [options.minIndexChange=1] - minimum route segment index change to consider for direction (index-based mode)
 * @param {number} [options.indexChangeThreshold=2] - route segment changes to consider significant
 */
function _RouteLineMover (
  callback,
  {
    reduceResources = false,
    noOptimisticMovement = false,
    maxDistanceToSnapToRouteline = 0.2,
    debug = false,
    minDirectionChangeDistance = 0.05,
    baseDirectionConfirmationCount = 4,
    midRouteDirectionConfirmationCount = 8,
    endRouteDirectionConfirmationCount = 8,
    endProximityThreshold = 0.15,
    maxSnapDistance = 0.5,
    transportationType = 'default',
    useIndexBasedDirection = true,
    minIndexChange = 2,
    indexChangeThreshold = 2
  } = {}) {
  const QUEUE_LIMIT = 8
  const AVERAGE_KM_PER_SECOND = 0.010
  const MAX_KM_PER_SECOND = 0.045
  // reduceResources is intended to avoid *all* extra movement work, including:
  // - requestAnimationFrame-based interpolation
  // - setInterval-based optimistic ("fake") movement between GPS updates
  const effectiveNoOptimisticMovement = Boolean(noOptimisticMovement || reduceResources)
  
  this.coordinateQueue = []
  this.distanceFromDestinationQueue = []
  this.timestampQueue = []
  this.currentCoordinate = null
  this.currentRouteCoordinate = null
  this.currentRouteCoordinateTimestamp = 0
  this.lineString = null
  this.moverTimerHandle = null
  this.kmetersPerFrequency = AVERAGE_KM_PER_SECOND
  this.lastLineDistance = 0
  this.currentLineDistance = 0
  this.lineStringDistance = 0
  this.actualCurrentLineDistance = 0
  this.routeLinePointChecker = null
  this.animateHandleObj = { handle: null }
  
  // Enhanced direction detection properties
  this.minDirectionChangeDistance = minDirectionChangeDistance
  this.baseDirectionConfirmationCount = baseDirectionConfirmationCount
  this.midRouteDirectionConfirmationCount = midRouteDirectionConfirmationCount
  this.endRouteDirectionConfirmationCount = endRouteDirectionConfirmationCount
  this.endProximityThreshold = endProximityThreshold
  this.maxSnapDistance = maxSnapDistance
  this.directionChangeBuffer = []
  this.lastConfirmedDirection = null
  this.snapDistanceHistory = []
  this.stabilityWindow = 5
  this.routeEndpoints = { start: null, end: null }
  this.transportationType = transportationType
  this.debug = debug
  
  // Index-based direction detection properties (NEW)
  this.routeIndexQueue = []               // Track route segment indices
  this.minIndexChange = minIndexChange    // Minimum index change to consider (segments)
  this.currentRouteIndex = null           // Current position index
  this.useIndexBasedDirection = useIndexBasedDirection      // Enable index-based detection by default
  this.indexChangeThreshold = indexChangeThreshold          // Threshold for significant index changes

  this.setRoute = (coordinatesArrayOrLineString) => {
    this.distanceFromDestinationQueue = []

    if (_.isArray(coordinatesArrayOrLineString)) {
      this.lineString = turfHelpers.lineString(coordinatesArrayOrLineString)
    } else {
      this.lineString = coordinatesArrayOrLineString
    }

    this.routeLinePointChecker = new _RouteLinePointChecker(this.lineString, maxDistanceToSnapToRouteline)
    this.lineStringDistance = turfLength(this.lineString)
    
    // Reset enhanced direction detection state
    this.directionChangeBuffer = []
    this.lastConfirmedDirection = null
    this.routeEndpoints = { start: null, end: null }
    
    // Reset index-based detection state (NEW)
    this.routeIndexQueue = []
    this.currentRouteIndex = null
    
    // Cache route endpoints for efficient access
    if (this.lineString && this.lineString.geometry && this.lineString.geometry.coordinates) {
      const coords = this.lineString.geometry.coordinates
      this.routeEndpoints.start = turfHelpers.point(coords[0])
      this.routeEndpoints.end = turfHelpers.point(coords[coords.length - 1])
    }
  }

  this.setCurrentCoordinate = (longLatArray) => {
    // Enhanced snap distance tracking
    const originalCoordinate = _.isArray(longLatArray) ? longLatArray : [longLatArray.lng, longLatArray.lat]
    
    // Calculate snap distance to track GPS reliability AND route index
    let snapDistance = 0
    let routeIndex = null
    
    if (this.lineString) {
      const nearestPoint = turfNearestPointOnLine(this.lineString, turfHelpers.point(originalCoordinate))
      snapDistance = nearestPoint.properties.dist
      routeIndex = nearestPoint.properties.index  // *** KEY: Extract route segment index ***
      
      // Store current route index for index-based direction detection
      if (this.useIndexBasedDirection) {
        this.currentRouteIndex = routeIndex
        this._addToRouteIndexQueue(routeIndex)
        
        if (this.debug) {
          console.log(`Route index: ${routeIndex}, snap distance: ${snapDistance.toFixed(3)}km`)
        }
      }
      
      // Track snap distance history
      this.snapDistanceHistory.push(snapDistance)
      if (this.snapDistanceHistory.length > this.stabilityWindow) {
        this.snapDistanceHistory.shift()
      }
      
      // If GPS is too far from route, don't update direction
      if (snapDistance > this.maxSnapDistance) {
        if (this.debug) {
          console.log(`GPS too far from route: ${snapDistance.toFixed(3)}km > ${this.maxSnapDistance}km`)
        }
        // Return early without updating direction
        callback(originalCoordinate, {
          isPositiveDirection: this.lastConfirmedDirection,
          isMoving: false,
          isInAnimation: false,
          isOnRoute: false,
          speedPerHour: 0,
          snapDistance: snapDistance,
          routeIndex: routeIndex
        })
        return
      }
    }
    
    this.currentCoordinate = turfHelpers.point(this.routeLinePointChecker.getClosestRouteLineWithinRadius(longLatArray))

    this.stopMover()

    if (this.animateHandleObj.handle) {
      if (debug) {
        console.log('cancelling animation frame')
      }
      cancelAnimationFrame(this.animateHandleObj.handle)
      this.animateHandleObj.handle = null
    }

    let uncompensatedCoordinate

    if (this._isJumpedUnnaturally(this.lineString, this.currentCoordinate)) {
      if (this.coordinateQueue.length) {
        if (debug) {
          console.log('compensated for unnatural jump')
        }
        uncompensatedCoordinate = _.cloneDeep(this.currentCoordinate)
        // use last known good coordinate instead and move ahead slightly
        this.currentCoordinate.geometry.coordinates = this._moveCoordinatesSlightlyAhead(this.currentRouteCoordinate)
        // this.currentCoordinate.geometry.coordinates = this.currentRouteCoordinate
      } else {
        if (debug) {
          console.log('compensated for unnatural jump at start of route')
        }
        // is at beginning of route so set user to that "unnatural" coordinate anyway
        uncompensatedCoordinate = _.cloneDeep(this.currentCoordinate)
        // this.currentCoordinate.geometry.coordinates = this.lineString.geometry.coordinates[0]
      }
    }

    const isPositiveDirection = this._isPositiveDirection()

    if (reduceResources) {
      callback(this.currentCoordinate.geometry.coordinates, {
        isPositiveDirection,
        isMoving: this.isMoving(),
        isInAnimation: false,
        isOnRoute: this.isOnRoute(),
        speedPerHour: this.speedPerHour()
      })
    } else {
      if (this.currentRouteCoordinate && (this.coordinateQueue.length === 1 ||
        (this.coordinateQueue.length > 1 && this.currentRouteCoordinateTimestamp >= this.coordinateQueue[this.coordinateQueue.length - 1].timestamp))) {
        if (debug) {
          console.log('animating from route coordinate')
        }

        _animateOnLine(
          this.currentRouteCoordinate,
          this.currentCoordinate.geometry.coordinates,
          this.animateHandleObj,
          callback,
          isPositiveDirection,
          _isOnRoute(this.lineString, this.currentCoordinate.geometry.coordinates),
          undefined,
          this.isMoving(),
          this.speedPerHour()
        )
      } else if (this.coordinateQueue.length > 1) {
        if (debug) {
          console.log('animating from coordinate to coordinate')
        }

        _animateOnLine(
          this.coordinateQueue[this.coordinateQueue.length - 2].geometry.coordinates,
          this.currentCoordinate.geometry.coordinates,
          this.animateHandleObj,
          callback,
          isPositiveDirection,
          _isOnRoute(this.lineString, this.coordinateQueue[this.coordinateQueue.length - 2].geometry.coordinates),
          undefined,
          this.isMoving(),
          this.speedPerHour()
        )
      } else {
        const distanceFromRouteLine = turfDistance(this.lineString.geometry.coordinates[0], this.currentCoordinate)

        if (distanceFromRouteLine > maxDistanceToSnapToRouteline) {
          if (debug) {
            console.log('starting away from of line')
          }

          callback(this.currentCoordinate.geometry.coordinates, {
            isPositiveDirection,
            isInAnimation: Boolean(!this.moverTimerHandle),
            isOnRoute: false,
            isMoving: false,
            speedPerHour: 0
          })
        } else {
          if (debug) {
            console.log('animating from start of line')
          }

          _animateOnLine(
            this.lineString.geometry.coordinates[0],
            this.currentCoordinate.geometry.coordinates,
            this.animateHandleObj,
            callback,
            isPositiveDirection,
            _isOnRoute(this.lineString, this.currentCoordinate.geometry.coordinates),
            undefined,
            this.isMoving(),
            this.speedPerHour()
          )
        }
      }
    }

    // if the current coordinate was compensated due to "unnaturally jumping", we still want to retain the original
    this._addToCoordinateQueue(uncompensatedCoordinate ? uncompensatedCoordinate : this.currentCoordinate)
    this.currentRouteCoordinateTimestamp = Date.now()
    this._addToTimestampQueue(this.currentRouteCoordinateTimestamp)
    this._calculateKmetersPerFrequency()
    this.currentRouteCoordinate = this.currentCoordinate.geometry.coordinates
    this.currentCoordinate.timestamp = this.currentRouteCoordinateTimestamp

    if (uncompensatedCoordinate) {
      uncompensatedCoordinate.timestamp = this.currentRouteCoordinateTimestamp
    }

    // if using a system where you want to "fill in" callbacks of coordinates between longer than 1sec intervals use this
    if (this.isOnRoute()) {
      if (debug) {
        console.log('is on route')
      }

      this._addToDistanceFromDestinationQueue(this.currentCoordinate.geometry.coordinates)
      this.actualCurrentLineDistance = _distanceTravelled(this.lineString, this.currentCoordinate)

      if (this.actualCurrentLineDistance > this.currentLineDistance) {
        this.currentLineDistance = this.actualCurrentLineDistance
      }

      const updatedIsPositiveDirection = this._isPositiveDirection()

      if (!effectiveNoOptimisticMovement && this.isMoving() &&
        ((updatedIsPositiveDirection && this.currentLineDistance <= this.actualCurrentLineDistance) ||
          (!updatedIsPositiveDirection && this.currentLineDistance > this.actualCurrentLineDistance))) {
        this._initMover()
      }
    } else {
      if (debug) {
        console.log('is not on route')
      }

      // callback(this.currentCoordinate.geometry.coordinates, {
      //   isPositiveDirection,
      //   isInAnimation: Boolean(!this.moverTimerHandle),
      //   isOnRoute: false,
      //   isMoving: false
      // })
    }
  }

  this._isJumpedUnnaturally = () => {
    if (this.coordinateQueue.length) {
      const previousCoordinate = this.coordinateQueue[this.coordinateQueue.length - 1]

      if (!_isOnRoute(this.lineString, previousCoordinate) && _isOnRoute(this.lineString, this.currentCoordinate)) {
        return false
      }

      const subPreviousCoordinate = this.coordinateQueue.length > 1 ? this.coordinateQueue[this.coordinateQueue.length - 2] : null
      const timestampDiffSec = (Date.now() - previousCoordinate.timestamp) / 1000
      const maxDistance = MAX_KM_PER_SECOND * timestampDiffSec
      const previousDistance = turfDistance(previousCoordinate.geometry.coordinates, this.currentCoordinate.geometry.coordinates)
      previousCoordinate.jumpedUnnaturally = previousDistance > maxDistance

      // if previous coordinate (and no previous 2 coordinates or previous 2 did not) did not jump unnaturally, then return false
      if (this.currentCoordinate.geometry.coordinates.join('') === previousCoordinate.geometry.coordinates.join('') ||
        (!previousCoordinate.jumpedUnnaturally && (!subPreviousCoordinate || !subPreviousCoordinate.jumpedUnnaturally))) {
        return false
      }
    } else if (turfDistance(this.lineString.geometry.coordinates[0], this.currentCoordinate.geometry.coordinates) <= 0.005) {
      return false
    }

    return true
  }

  this.isMoving = () => {
    let isMoving = false
    if (this.coordinateQueue.length >= 2) {
      const movingLine = turfHelpers.lineString(this.coordinateQueue.map(o => (o.geometry.coordinates)))
      // STATIONARY VEHICLE FIX: Increase movement threshold from 10m to 25m to filter GPS noise
      isMoving = turfLength(movingLine) > 0.025  // 25 meters instead of 10 meters
    }
    return isMoving
  }

  this.speedPerHour = () => {
    let speedPerHour = 0

    if (this.timestampQueue.length > 2 && this.coordinateQueue.length > 2) {
      const diffInMs = (this.timestampQueue[this.timestampQueue.length - 1] - this.timestampQueue[0])
      const movingLine = turfHelpers.lineString(this.coordinateQueue.map(o => (o.geometry.coordinates)))
      const totalDistance = turfLength(movingLine)
      speedPerHour = _.round(totalDistance / (diffInMs / 1000 / 60 / 60), 3)
    }

    return speedPerHour
  }

  this.isOnRoute = () => {
    if (this.lineString && this.currentCoordinate) {
      return _isOnRoute(this.lineString, this.currentCoordinate)
    }
    return false
  }

  this.stopMover = () => {
    if (this.moverTimerHandle) {
      clearInterval(this.moverTimerHandle)
      this.moverTimerHandle = null
    }
  }

  this._isPositiveDirection = () => {
    // Use index-based or distance-based detection based on configuration
    if (this.useIndexBasedDirection) {
      return this._isPositiveDirectionIndexBased()
    } else {
      return this._isPositiveDirectionDistanceBased()
    }
  }

  /**
   * INDEX-BASED direction detection (DEFAULT - more performant and accurate)
   * Uses route segment indices from turfNearestPointOnLine for direction detection
   */
  this._isPositiveDirectionIndexBased = () => {
    // If not enough data, maintain last confirmed direction
    if (this.routeIndexQueue.length < 2) {
      return this.lastConfirmedDirection
    }

    // Calculate average snap distance to determine GPS reliability
    if (this.snapDistanceHistory.length > 0) {
      const avgSnapDistance = this.snapDistanceHistory.reduce((a, b) => a + b, 0) / this.snapDistanceHistory.length
      
      // If GPS coordinates are consistently far from route, don't change direction
      if (avgSnapDistance > this.maxSnapDistance * 0.7) {
        if (this.debug) {
          console.log(`High snap distance average: ${avgSnapDistance.toFixed(3)}km - maintaining direction (index-based)`)
        }
        return this.lastConfirmedDirection
      }
    }

    // Calculate direction based on significant index changes only
    let confirmedPositive = 0
    let significantChanges = 0
    
    for (let i = 1; i < this.routeIndexQueue.length; i++) {
      const indexChange = this.routeIndexQueue[i] - this.routeIndexQueue[i - 1]
      
      // Only consider significant index changes to avoid GPS noise
      if (Math.abs(indexChange) >= this.minIndexChange) {
        significantChanges++
        if (indexChange > 0) {
          confirmedPositive++  // Moving towards end (higher index)
        } else {
          confirmedPositive--  // Moving towards start (lower index)
        }
      }
    }

    // If no significant changes detected, maintain current direction
    if (significantChanges === 0) {
      return this.lastConfirmedDirection
    }

    // Determine tentative direction
    let tentativeDirection
    if (confirmedPositive > 0) {
      tentativeDirection = true   // Positive direction (towards end)
    } else if (confirmedPositive < 0) {
      tentativeDirection = false  // Negative direction (towards start)
    } else {
      tentativeDirection = this.lastConfirmedDirection // Neutral, maintain current
    }

    // Apply adaptive confirmation buffer logic
    return this._confirmDirectionChange(tentativeDirection, 'index-based')
  }

  /**
   * DISTANCE-BASED direction detection (legacy - for backwards compatibility)
   * Uses distance calculations for direction detection
   */
  this._isPositiveDirectionDistanceBased = () => {
    // If not enough data, maintain last confirmed direction
    if (this.distanceFromDestinationQueue.length < 2) {
      return this.lastConfirmedDirection
    }

    // Calculate average snap distance to determine GPS reliability
    if (this.snapDistanceHistory.length > 0) {
      const avgSnapDistance = this.snapDistanceHistory.reduce((a, b) => a + b, 0) / this.snapDistanceHistory.length
      
      // If GPS coordinates are consistently far from route, don't change direction
      if (avgSnapDistance > this.maxSnapDistance * 0.7) {
        if (this.debug) {
          console.log(`High snap distance average: ${avgSnapDistance.toFixed(3)}km - maintaining direction (distance-based)`)
        }
        return this.lastConfirmedDirection
      }
    }

    // Calculate direction based on significant distance changes only
    let confirmedPositive = 0
    let significantChanges = 0
    
    for (let i = 1; i < this.distanceFromDestinationQueue.length; i++) {
      const distanceChange = this.distanceFromDestinationQueue[i] - this.distanceFromDestinationQueue[i - 1]
      
      // Only consider significant distance changes to avoid GPS noise
      if (Math.abs(distanceChange) >= this.minDirectionChangeDistance) {
        significantChanges++
        if (distanceChange < 0) {
          confirmedPositive++  // Moving towards destination
        } else {
          confirmedPositive--  // Moving away from destination
        }
      }
    }

    // If no significant changes detected, maintain current direction
    if (significantChanges === 0) {
      return this.lastConfirmedDirection
    }

    // Determine tentative direction
    let tentativeDirection
    if (confirmedPositive > 0) {
      tentativeDirection = true   // Positive direction
    } else if (confirmedPositive < 0) {
      tentativeDirection = false  // Negative direction
    } else {
      tentativeDirection = this.lastConfirmedDirection // Neutral, maintain current
    }

    // Apply adaptive confirmation buffer logic
    return this._confirmDirectionChange(tentativeDirection, 'distance-based')
  }

  /**
   * Common confirmation logic for both detection methods
   */
  this._confirmDirectionChange = (tentativeDirection, detectionMethod) => {
    // *** KEY ENHANCEMENT: Adaptive confirmation buffer based on position ***
    const requiredConfirmations = this._getRequiredConfirmationsBasedOnPosition()
    
    if (this.debug) {
      console.log(`Required confirmations: ${requiredConfirmations} (${detectionMethod})`)
    }

    // Add to direction change buffer for confirmation
    this.directionChangeBuffer.push(tentativeDirection)
    
    // Keep buffer size limited to maximum required confirmations
    const maxBuffer = Math.max(this.baseDirectionConfirmationCount, this.midRouteDirectionConfirmationCount, this.endRouteDirectionConfirmationCount)
    if (this.directionChangeBuffer.length > maxBuffer) {
      this.directionChangeBuffer.shift()
    }

    // Require consistent direction readings to confirm direction change
    if (this.directionChangeBuffer.length >= requiredConfirmations) {
      // Check only the last N readings where N = requiredConfirmations
      const recentReadings = this.directionChangeBuffer.slice(-requiredConfirmations)
      const allSame = recentReadings.every(dir => dir === recentReadings[0])
      
      if (allSame) {
        const newDirection = recentReadings[0]
        
        // Only update if direction actually changed
        if (newDirection !== this.lastConfirmedDirection) {
          if (this.debug) {
            console.log(`Direction change confirmed: ${this.lastConfirmedDirection} -> ${newDirection} (${requiredConfirmations} confirmations, ${detectionMethod})`)
          }
          this.lastConfirmedDirection = newDirection
        } else {
          // Direction confirmed as stable
          this.lastConfirmedDirection = newDirection
        }
      }
    }

    return this.lastConfirmedDirection
  }

  /**
   * Determine required confirmations based on current position relative to route endpoints
   */
  this._getRequiredConfirmationsBasedOnPosition = () => {
    if (!this.currentCoordinate || !this.lineString) {
      return this.baseDirectionConfirmationCount
    }

    // Get transportation-specific threshold (convert to km)
    const TRANSPORTATION_THRESHOLDS = {
      bus: 150,           // Urban bus routes
      rail: 200,          // Metro/rail systems
      delivery: 100,      // Delivery routes
      rideshare: 75,      // Ride-sharing/taxi
      highway: 250,       // Highway/express routes
      default: 150        // General purpose default
    }
    
    const thresholdMeters = TRANSPORTATION_THRESHOLDS[this.transportationType] || TRANSPORTATION_THRESHOLDS.default
    const thresholdKm = thresholdMeters / 1000
    
    // Override with explicit threshold if provided
    const proximityThreshold = this.endProximityThreshold || thresholdKm

    // Get route endpoints
    const routeCoords = this.lineString.geometry.coordinates
    const startPoint = turfHelpers.point(routeCoords[0])
    const endPoint = turfHelpers.point(routeCoords[routeCoords.length - 1])
    
    // Calculate distances to both endpoints
    const distanceToStart = turfDistance(this.currentCoordinate, startPoint, { units: 'kilometers' })
    const distanceToEnd = turfDistance(this.currentCoordinate, endPoint, { units: 'kilometers' })
    
    // Check position on route: start, end, or middle
    const nearStart = distanceToStart <= proximityThreshold
    const nearEnd = distanceToEnd <= proximityThreshold
    
    if (nearStart) {
      // Near route START: use fast confirmation (quick turnaround detection for boarding)
      if (this.debug) {
        console.log(`Near route start (${distanceToStart.toFixed(3)}km, threshold: ${proximityThreshold.toFixed(3)}km) - using fast confirmation`)
      }
      return this.baseDirectionConfirmationCount
    } else if (nearEnd) {
      // Near route END: use conservative confirmation (avoid false positives during terminal activities)
      if (this.debug) {
        console.log(`Near route end (${distanceToEnd.toFixed(3)}km, threshold: ${proximityThreshold.toFixed(3)}km) - using conservative confirmation`)
      }
      return this.endRouteDirectionConfirmationCount
    } else {
      // Middle of route: use conservative confirmation (false positives most likely)
      if (this.debug) {
        const distance = Math.min(distanceToStart, distanceToEnd)
        console.log(`Middle of route (${distance.toFixed(3)}km from nearest end, threshold: ${proximityThreshold.toFixed(3)}km) - using conservative confirmation`)
      }
      return this.midRouteDirectionConfirmationCount
    }
  }

  this._addToTimestampQueue = (timestamp) => {
    if (this.timestampQueue.length === QUEUE_LIMIT) {
      for (let i = 1; i < QUEUE_LIMIT; i++) {
        this.timestampQueue[i - 1] = this.timestampQueue[i]
      }
      this.timestampQueue[QUEUE_LIMIT - 1] = timestamp
    } else {
      this.timestampQueue.push(timestamp)
    }
  }

  this._calculateKmetersPerFrequency = () => {
    if (this.timestampQueue.length > 1 && this.coordinateQueue.length > 1) {
      const diffInMs = (this.timestampQueue[this.timestampQueue.length - 1] - this.timestampQueue[0])
      if (diffInMs) {
        const movingLine = turfHelpers.lineString(this.coordinateQueue.map(o => (o.geometry.coordinates)))
        let kmLength = turfLength(movingLine)
        this.kmetersPerFrequency = kmLength / (diffInMs / 1000) // frequency is in seconds
        // prevent too small a frequency
        this.kmetersPerFrequency = this.kmetersPerFrequency < 0.001 ? 0.001 : this.kmetersPerFrequency
        // prevent ridiculous speeds
        this.kmetersPerFrequency = this.kmetersPerFrequency > MAX_KM_PER_SECOND ? MAX_KM_PER_SECOND : this.kmetersPerFrequency
      }
    } else {
      this.kmetersPerFrequency = AVERAGE_KM_PER_SECOND
    }
  }

  this._addToCoordinateQueue = (value) => {
    if (this.coordinateQueue.length === QUEUE_LIMIT) {
      for (let i = 1; i < QUEUE_LIMIT; i++) {
        this.coordinateQueue[i - 1] = this.coordinateQueue[i]
      }
      this.coordinateQueue[QUEUE_LIMIT - 1] = value
    } else {
      this.coordinateQueue.push(value)
    }
  }

  this._addToDistanceFromDestinationQueue = (coordinates) => {
    if (this.lineString) {
      const slicedLineLength = _distanceTravelled(this.lineString, this.lineString.geometry.coordinates[this.lineString.geometry.coordinates.length - 1], coordinates)
      const DISTANCE_QUEUE_LIMIT = QUEUE_LIMIT
      
      if (this.distanceFromDestinationQueue.length === DISTANCE_QUEUE_LIMIT) {
        for (let i = 1; i < DISTANCE_QUEUE_LIMIT; i++) {
          this.distanceFromDestinationQueue[i - 1] = this.distanceFromDestinationQueue[i]
        }
        
        this.distanceFromDestinationQueue[DISTANCE_QUEUE_LIMIT - 1] = slicedLineLength
      } else {
        this.distanceFromDestinationQueue.push(slicedLineLength)
      }
    }
  }

  this._addToRouteIndexQueue = (routeIndex) => {
    const INDEX_QUEUE_LIMIT = QUEUE_LIMIT  // Same as coordinate queue limit
    
    if (this.routeIndexQueue.length === INDEX_QUEUE_LIMIT) {
      for (let i = 1; i < INDEX_QUEUE_LIMIT; i++) {
        this.routeIndexQueue[i - 1] = this.routeIndexQueue[i]
      }
      this.routeIndexQueue[INDEX_QUEUE_LIMIT - 1] = routeIndex
    } else {
      this.routeIndexQueue.push(routeIndex)
    }
  }

  this._moveCoordinatesSlightlyAhead = (existingCoordinates, maxKmToMoveAhead) => {
    let newCoordinates = existingCoordinates
    maxKmToMoveAhead = maxKmToMoveAhead || (MIN_DISTANCE_ARRIVAL_DETECTION - 0.005)
    const kmetersPerFrequency = this.kmetersPerFrequency > maxKmToMoveAhead ? maxKmToMoveAhead :
      (this.kmetersPerFrequency < 0 ? 0 : this.kmetersPerFrequency)

    if (kmetersPerFrequency && this.actualCurrentLineDistance && this.actualCurrentLineDistance > 0.03 &&
      this.actualCurrentLineDistance + kmetersPerFrequency < this.lineStringDistance) {

      if (debug) {
        console.log('moved ahead slightly', this.kmetersPerFrequency + 'meters')
      }

      let currentLineDistance = this.actualCurrentLineDistance + kmetersPerFrequency
      const slicedLine = turfLineSliceAlong(this.lineString, 0, currentLineDistance)

      newCoordinates = slicedLine.geometry.coordinates[slicedLine.geometry.coordinates.length - 1]
    }

    return newCoordinates
  }

  this._initMover = () => {
    this.stopMover()

    if (effectiveNoOptimisticMovement) {
      return
    }

    let totalDistanceCovered = 0

    this.moverTimerHandle = setInterval(() => {
      if (!this.lineString) {
        clearInterval(this.moverTimerHandle)
        this.moverTimerHandle = null
        throw new Error('no line string object or coordinates of route line provided')
      }

      const isPositiveDirection = this._isPositiveDirection()

      if (this.isMoving() && isPositiveDirection === true && totalDistanceCovered <= 0.05) {
        if (debug) {
          console.log('is moving')
        }

        let slicedLine

        // we check if distance travelled is more than 50m in order to start the mover and we also forsee ahead 50m with distance travelled to make sure that the optimized mover does not continue when it is near the destination
        if (this.currentLineDistance && this.currentLineDistance > 0.03 &&
          this.currentLineDistance + 0.05 < this.lineStringDistance) {
          if (debug) {
            console.log('moving optimistically')
          }
          const backedUpCurrentLineDistance = this.currentLineDistance
          totalDistanceCovered += this.kmetersPerFrequency
          this.currentLineDistance += this.kmetersPerFrequency
          // decided to NOT move in opposite direction of route line optimistically, if want to go on different route, create a new route from current position
          this.lastLineDistance = backedUpCurrentLineDistance
          if (this.currentLineDistance !== this.lastLineDistance) {
            slicedLine = turfLineSliceAlong(this.lineString, 0, this.currentLineDistance)
          }
        } else {
          this.stopMover()
        }

        if (slicedLine) {
          this.currentRouteCoordinateTimestamp = Date.now()
          const snapped = turfNearestPointOnLine(slicedLine, this.currentCoordinate.geometry.coordinates)
          this.currentRouteCoordinate = snapped.geometry.coordinates // slicedLine.geometry.coordinates[slicedLine.geometry.coordinates.length - 1]
          // console.warn('snapping to ', this.currentRouteCoordinate, 'instead of ', slicedLine.geometry.coordinates[slicedLine.geometry.coordinates.length - 1])
        }
      } else {
        this.stopMover()
      }

      callback(this.currentRouteCoordinate, {
        isPositiveDirection,
        isInAnimation: false, // Boolean(!this.moverTimerHandle),
        isOnRoute: true,
        isMoving: this.isMoving()
      })
    }, 1000)
  }
}

/**
 *
 * @param {number} maxSampleSet=5 - The number of coordinates to collect with `isPassengerOnTransportation` before performing a check, the larger the sample set, the more accurate (however the more infrequent an answer is provided)
 * @param {number} withinRadius=30 - The number for the size of the radius to check for a passenger coordinates to be within the vicinity of the transportation coordinates
 * @param {string} radiusUnits='meters' - The units representing the radius size amount given. Can be degrees, radians, miles, kilometers, inches, yards, meters
 */
function _PassengerOnTransportationChecker (maxSampleSet = 5, withinRadius = 30, radiusUnits = 'meters') {
  this.sampleSet = []
  this.lastSample = null
  this.currentCheckResult = false
  this.isPassengerOnTransportation = (passengerCoordinates, transportationCoordinates) => {
    const parsedPassengerCoordinates = _.isArray(passengerCoordinates) ? passengerCoordinates : [passengerCoordinates.lng, passengerCoordinates.lat]
    const parsedTransportationCoordinates = _.isArray(transportationCoordinates) ? transportationCoordinates : [transportationCoordinates.lng, transportationCoordinates.lat]
    const currentTimestamp = new Date().getTime()
    this.sampleSet.push({
      currentTimestamp,
      passengerCoordinates: parsedPassengerCoordinates,
      transportationCoordinates: parsedTransportationCoordinates
    })
    if (this.sampleSet.length === maxSampleSet) {
      this.currentCheckResult = _guessIfOnTransportation(this.sampleSet, this.lastSample, withinRadius, radiusUnits)
      this.lastSample = this.sampleSet[this.sampleSet.length - 1]
      this.sampleSet = []
    }
    return this.currentCheckResult
  }
}

/**
 * Determine approx. distance of coordinates point on route line
 * @deprecated - use distanceTravelled instead
 * @param {array} routeLineLegs - array of route "leg" objects found in each itinerary object
 * @param {*} currentCoordinates {array|object} - Current set of [lng, lat]/{lng, lat} to check on route line (within 500 meter radius) for distance travelled from beginning of route
 * @param {number} previousDistance - number that was returned from the previous iteration of the function, this prevents estimating a distance less than previously encountered. This would be in the measurement units specified for the function.
 * @param {*} previousCoordinates {array|object} - coordinates that were passed to the previous iteration of the function as 'currentCoordinates'
 * @param {string} units=kilometers - can be kilometers, miles, meters
 * @returns {number}
 */
function distanceOnRouteLine (routeLineLegs, currentCoordinates, previousDistance, previousCoordinates, units) {
  if (!routeLineLegs || !routeLineLegs.length || !currentCoordinates) {
    throw new Error('routeLineLegs and currentCoordinates params required')
  }
  const radiusDistance = 300
  let totalDistance = 0
  let discontinueDistanceCheck = false
  let parsedPreviousCoordinates, parsedPreviousDistance
  if (previousCoordinates) {
    parsedPreviousCoordinates = _.isArray(previousCoordinates) ? previousCoordinates : [previousCoordinates.lng, previousCoordinates.lat]
  }
  const parsedUnits = units || 'kilometers'
  if (previousDistance) {
    // convert back to meters
    switch (parsedUnits) {
      case 'kilometers':
        parsedPreviousDistance = previousDistance * 1000
        break
      case 'miles':
        parsedPreviousDistance = _milesToMeters(previousDistance)
        break
      default:
        parsedPreviousDistance = previousDistance
    }
  }
  let totalLegDistance = 0
  let totalStepDistance = 0
  const parsedCurrentCoordinates = _.isArray(currentCoordinates) ? currentCoordinates : [currentCoordinates.lng, currentCoordinates.lat]
  const withinRadiusParams1 = {
    lng: parsedCurrentCoordinates[0],
    lat: parsedCurrentCoordinates[1],
    distance: radiusDistance
  }
  routeLineLegs.forEach((leg) => {
    if (!discontinueDistanceCheck) {
      const steps = leg.steps
      if (steps.length) {
        for (let i = 0; i < steps.length; i++) {
          const stepCoordinateSets = _.get(steps[i], 'geometry.coordinates', [])
          const stepCoordinatesLength = stepCoordinateSets.length
          if (stepCoordinatesLength) {
            let a = 0
            for (a = 0; a < stepCoordinatesLength; a++) {
              const withinRadiusParams2 = {
                lng: stepCoordinateSets[a][0],
                lat: stepCoordinateSets[a][1],
                distance: radiusDistance
              }
              if (_isWithinRadius(withinRadiusParams1, withinRadiusParams2, 'meters')) {
                discontinueDistanceCheck = true
                break
              }
            }
            totalStepDistance += (a - 1 === stepCoordinatesLength ? steps[i].distance : (steps[i].distance / stepCoordinatesLength) * a)
          } else if (steps[i].lon) {
            totalStepDistance += steps[i].distance
            const withinRadiusParams2 = { lng: steps[i].lon, lat: steps[i].lat, distance: radiusDistance }
            const isWithinRadius = _isWithinRadius(withinRadiusParams1, withinRadiusParams2, 'meters')
            if (isWithinRadius) {
              discontinueDistanceCheck = true
            }
          }
          if (discontinueDistanceCheck) {
            break
          }
        }
        if (discontinueDistanceCheck) {
          totalDistance += totalStepDistance
        }
      } else if (leg.from) {
        const toWithinRadius = { lng: leg.to.lon, lat: leg.to.lat, distance: radiusDistance }
        totalLegDistance += leg.distance
        if (_isWithinRadius(withinRadiusParams1, toWithinRadius, 'meters')) {
          totalDistance += (totalLegDistance + totalStepDistance)
          discontinueDistanceCheck = true
        }
      }
      // no steps available? Then measure distance by "as the crow flies" from last know coordinate
      if (!discontinueDistanceCheck && parsedPreviousCoordinates) {
        // measure an "as the crow flies" distance
        const asCrowFliesDistance = turfDistance(parsedPreviousCoordinates, parsedCurrentCoordinates, { units: 'meters' })
        // console.log('parsedPreviousDistance', parsedPreviousDistance, 'asCrowFliesDistance', asCrowFliesDistance)
        totalDistance += ((parsedPreviousDistance ? parsedPreviousDistance : 0) + asCrowFliesDistance)
        discontinueDistanceCheck = true
      }
    }
  })
  // console.log('discontinueDistanceCheck', discontinueDistanceCheck, 'totalStepDistance', totalStepDistance, 'totalLegDistance', totalLegDistance, 'totalDistance', totalDistance)
  if (!discontinueDistanceCheck) {
    return previousDistance || 0
  } else {
    switch (parsedUnits) {
      case 'kilometers':
        totalDistance = _.round(totalDistance / 1000, 2)
        break
      case 'miles':
        totalDistance = _metersToMiles(totalDistance)
        break
    }
    if (previousDistance && totalDistance < previousDistance) {
      totalDistance = previousDistance
    }
    return totalDistance
  }
}

/**
 *
 * @param {array|object} routeCoordinates {LineString|[[lng, lat], ...]}
 * @param {array|object} currentLngLat=undefined {Point|{lng, lat}|[lng, lat]}
 * @param {array|object} startLngLat=undefined {Point|{lng, lat}|[lng, lat]}
 * @param {string} units='kilometers'
 * @returns {number}
 * @private
 */
function _distanceTravelled (routeCoordinates, currentLngLat, startLngLat, units = 'kilometers') {
  if (!routeCoordinates || (_.isArray(routeCoordinates) && !routeCoordinates.length)) {
    throw new Error('routeCoordinates params required')
  }
  const sliced = _sliceRoute(routeCoordinates, startLngLat, currentLngLat)
  return _.round(turfLength(sliced, { units }), 3)
}

/**
 *
 * @param {array|object} routeCoordinates
 * @param {array|object|{lng, lat}} startLngLat=undefined
 * @param {array|object|{lng, lat}} currentLngLat=undefined
 * @returns {Feature<LineString> | *} - access coordinates by geometry.coordinates
 */
function _sliceRoute (routeCoordinates, startLngLat = undefined, currentLngLat = undefined) {
  if (!routeCoordinates || (_.isArray(routeCoordinates) && !routeCoordinates.length)) {
    throw new Error('routeCoordinates params required')
  }
  let _routeLine = routeCoordinates
  if (_.isArray(routeCoordinates)) {
    _routeLine = turfHelpers.lineString(routeCoordinates)
  }

  let _startPoint, _endPoint
  if (startLngLat) {
    if (startLngLat.coordinates) {
      _startPoint = turfHelpers.point(startLngLat.coordinates)
    } else if (_.get(startLngLat, 'geometry.coordinates')) {
      _startPoint = currentLngLat
    } else if (_.isArray(startLngLat)) {
      _startPoint = turfHelpers.point(startLngLat)
    } else {
      _startPoint = turfHelpers.point([startLngLat.lng, startLngLat.lat])
    }
  } else {
    _startPoint = turfHelpers.point(_routeLine.geometry.coordinates[0])
  }
  if (currentLngLat) {
    if (currentLngLat.coordinates) {
      _endPoint = turfHelpers.point(currentLngLat.coordinates)
    } else if (_.get(currentLngLat, 'geometry.coordinates')) {
      _endPoint = currentLngLat
    } else if (_.isArray(currentLngLat)) {
      _endPoint = turfHelpers.point(currentLngLat)
    } else {
      _endPoint = turfHelpers.point([currentLngLat.lng, currentLngLat.lat])
    }
  } else {
    _endPoint = turfHelpers.point(_routeLine.geometry.coordinates[_routeLine.geometry.coordinates.length - 1])
  }
  return _lineSlice(_startPoint, _endPoint, _routeLine)
}

/**
 * Turf line slice seems to have a bug in the last couple of coordinates so this helps fix it
 * @param startPoint
 * @param endPoint
 * @param routeLine
 * @returns {Feature<LineString> | *}
 * @private
 */
function _lineSlice (startPoint, endPoint, routeLine) {
  let lineString = turfLineSlice(startPoint, endPoint, routeLine)
  let coordinates = lineString.geometry.coordinates
  if (coordinates.length > 1 && coordinates[coordinates.length - 2].join('') === coordinates[coordinates.length - 1].join('')) {
    coordinates.pop()
    // if the last entry has a different long coord than the second last entry
    if (coordinates.length > 1 && ((coordinates[coordinates.length - 1][0] > 0 && coordinates[coordinates.length - 2][0] < 0) || (coordinates[coordinates.length - 1][0] < 0 && coordinates[coordinates.length - 2][0] > 0))
    ) {
      coordinates[coordinates.length - 1][0] = coordinates[coordinates.length - 1][0] * -1
    }
  }
  lineString.geometry.coordinates = coordinates
  return lineString
}

/**
 * Generate a mapbox compatible route layer
 * @param {object} mapboxGeoJSON
 * @param {string} lineColor
 * @param {number} lineWidth
 * @param {object} extendObject
 * @returns {{id: string, type: string, source: {type: string, data: {type: string, properties: {}, geometry: *}}, layout: {'line-join': string, 'line-cap': string}, paint: {'line-opacity': number, 'line-color': *|string, 'line-width': *|number}}}
 */
function _generateRouteLayer (mapboxGeoJSON, lineColor, lineWidth, extendObject) {
  const routeLayerTemplate = {
    'id': 'route' + Date.now() + Math.random(),
    'type': 'line',
    'source': {
      'type': 'geojson',
      'data': {
        'type': 'Feature',
        'properties': {},
        'geometry': mapboxGeoJSON
      }
    },
    'layout': {
      'line-join': 'miter',
      'line-cap': 'round'
    },
    'paint': {
      'line-opacity': 0.8,
      'line-color': lineColor || 'rgb(38, 68, 207)',
      'line-width': lineWidth || 4
    }
  }

  if (extendObject) {
    let extendedObject = _.assignIn(routeLayerTemplate, extendObject)
    extendedObject.source.data.geometry = mapboxGeoJSON
    return extendedObject
  }
  return routeLayerTemplate
}

/**
 *
 * @param {object} lngLat - {lat, lng}
 * @param {array} arrayOfLngLats - array of objects or array of arrays [{lng, lat}|[float, float]]
 * @param {number} distance - defaults 0.05
 * @param {string} units - defaults to "kilometers"
 * @returns {array} - array of lng, lats as an array of arrays
 * @private
 */
function _getPointsWithinRadius (lngLat, arrayOfLngLats, distance, units) {
  if (!lngLat || !arrayOfLngLats || !Object.keys(lngLat).length) {
    throw new Error('Longitude and Latitude object required: {lng, lat}')
  }
  if (!arrayOfLngLats || !arrayOfLngLats.length) {
    throw new Error('Array if Longitude and Latitude objects or arrays required, [{lng, lat}, ...] or [[x, x], ...]')
  }
  const parsedUnit = units || 'kilometers'
  const parsedDistance = distance || 0.05
  let parsedArrayOfLngLats = []
  arrayOfLngLats.forEach((item) => {
    let parsedItem = item
    if (!_.isArray(item)) {
      parsedItem = [item.lng, item.lat]
    }
    parsedArrayOfLngLats.push(parsedItem)
  })
  const points = turfHelpers.points(parsedArrayOfLngLats)
  const circlePolygon = turfCircle([typeof lngLat.lng === 'string' ? Number(lngLat.lng) : lngLat.lng, typeof lngLat.lat === 'string' ? Number(lngLat.lat) : lngLat.lat], parsedDistance, { units: parsedUnit })
  const pointsWithin = turfPointsWithinPolygon(points, circlePolygon)
  let arrayOfPoints = []
  if (pointsWithin && pointsWithin.features.length) {
    const isArray = _.isArray(arrayOfLngLats[0])
    pointsWithin.features.forEach((feature) => {
      if (isArray) {
        arrayOfPoints.push(feature.geometry.coordinates)
      } else {
        arrayOfPoints.push({ lng: feature.geometry.coordinates[0], lat: feature.geometry.coordinates[1] })
      }
    })
  }
  return arrayOfPoints
}

/**
 *
 * @param {[number, number][]} arrayOfLngLats
 * @param {array} arrayOfPolygonLngLats
 * @returns {*[]}
 * @private
 */
function _getPointsWithinPolygon (arrayOfLngLats, arrayOfPolygonLngLats) {
  if (!arrayOfLngLats || !arrayOfLngLats.length) {
    throw new Error('Array if Longitude and Latitude objects or arrays to check in polygon required, [{lng, lat}, ...] or [[x, x], ...]')
  }
  if (!arrayOfPolygonLngLats || !arrayOfPolygonLngLats.length) {
    throw new Error('Array if Longitude and Latitude objects/arrays representing polygon required, [{lng, lat}, ...] or [[x, x], ...]')
  }
  let parsedArrayOfLngLats = []
  arrayOfLngLats.forEach((item) => {
    let parsedItem = item
    if (!_.isArray(item)) {
      parsedItem = [item.lng, item.lat]
    }
    parsedArrayOfLngLats.push(parsedItem)
  })
  let parsedArrayOfPolygonLngLats = []
  arrayOfPolygonLngLats.forEach((item) => {
    let parsedItem = item
    if (!_.isArray(item)) {
      parsedItem = [item.lng, item.lat]
    }
    parsedArrayOfPolygonLngLats.push(parsedItem)
  })
  const points = turfHelpers.points(parsedArrayOfLngLats)
  const polygon = turfHelpers.polygon(parsedArrayOfPolygonLngLats)
  const pointsWithin = turfPointsWithinPolygon(points, polygon)
  let arrayOfPoints = []
  if (pointsWithin && pointsWithin.features.length) {
    const isArray = _.isArray(arrayOfLngLats[0])
    pointsWithin.features.forEach((feature) => {
      if (isArray) {
        arrayOfPoints.push(feature.geometry.coordinates)
      } else {
        arrayOfPoints.push({ lng: feature.geometry.coordinates[0], lat: feature.geometry.coordinates[1] })
      }
    })
  }
  return arrayOfPoints
}

/**
 *
 * @param {[number, number]} arrayOfLngLats
 * @param {[number, number][]} arrayOfBoundsLngLats
 * @returns {*[]}
 */
function getParsedBoundsArrayForPolygon (arrayOfLngLats, arrayOfBoundsLngLats) {
  if (!arrayOfLngLats || !arrayOfLngLats.length) {
    throw new Error('Array if Longitude and Latitude objects or arrays to check in polygon required, [{lng, lat}, ...] or [[x, x], ...]')
  }
  if (!arrayOfBoundsLngLats || !arrayOfBoundsLngLats.length || arrayOfBoundsLngLats.length < 2) {
    throw new Error('Array if Longitude and Latitude objects/arrays representing Bounds required, [{lng, lat}, ...] or [[x, x], ...] or its length must be greater then 2.')
  }

  let parseBoundsArrayForPolygon = []
  _.times(2, function (item) {
    parseBoundsArrayForPolygon.push(arrayOfBoundsLngLats[0][item])
    parseBoundsArrayForPolygon.push(arrayOfBoundsLngLats[1][item])
  })

  const boundsGeometry = turfBboxPolygon(parseBoundsArrayForPolygon)
  let arrayOfPoint = []
  if (_.get(boundsGeometry, 'geometry.coordinates', []).length) {
    arrayOfPoint = _getPointsWithinPolygon([arrayOfLngLats], boundsGeometry.geometry.coordinates)
  }
  return arrayOfPoint
}

function _createUniqueIdentifierFromCoordinates (coordinates) {
  // , precision = 7
  //return coordinates.map(coordinate => coordinate.map(number => number.toFixed(precision)).join(',')).join(';')
  return coordinates.map(coordinate => coordinate.join(',')).join(';')
}


/**
 * Generate a unique string to represent a route for transportation
 * @param {array|object} route - array of coordinates, mapbox route or the 'legs' property from an OTP route results object. Usually found in: plan -> itineraries[x]  -> legs
 * @param {string} routerId - the region if defined (eg. accra)
 * @param {object} [options] - options object
 * @param {(string|number)[]} [options.from] - from coordinates - longitude, latitude
 * @param {(string|number)[]} [options.to] - to coordinates - longitude, latitude
 * @returns {string} - concatenated string to make up the unique id of this route. It excludes any route leg item that is not mode = 'BUS'
 * @private
 */
function _generateRouteIdList (route, routerId = undefined, options = { from: null, to: null }) {
  if (route) {
    let routeIdListObj = { geometryHash: [], routerId }

    const from = options.from ? { lon: options.from[0], lat: options.from[1] } : undefined
    const to = options.to ? { lon: options.to[0], lat: options.to[1] } : undefined

    if (Array.isArray(route) && Array.isArray(route[0])) {
      // array of coordinates
      routeIdListObj = _generateRouteIdListFromCoordinates({
        from: from || { lon: route[0][0], lat: route[0][1] },
        to: to || { lon: route[route.length - 1][0], lat: route[route.length - 1][1] },
        coordinates: route,
        routerId
      })
    } else if (!Array.isArray(route) && typeof route === 'object' && route.geometry && route.geometry.coordinates) {
      routeIdListObj = _generateMapboxRouteIdList(route, options)
    } else {
      route.forEach((leg, key) => {
        if (key === 0) {
          routeIdListObj.from = from || { lon: leg.from.lon, lat: leg.from.lat }
        }

        if (key === route.length - 1) {
          routeIdListObj.to = to || { lon: leg.to.lon, lat: leg.to.lat }
        }

        if (leg.legGeometry && leg.legGeometry.points) {
          routeIdListObj.geometryHash = routeIdListObj.geometryHash.concat(_convertPolylineToCoordinates(leg.legGeometry.points))
        }
      })

      if (routeIdListObj.geometryHash.length) {
        routeIdListObj.geometryHash = md5(_createUniqueIdentifierFromCoordinates(routeIdListObj.geometryHash))
      } else {
        routeIdListObj.geometryHash = ''
      }

      routeIdListObj = stableStringify(routeIdListObj)
    }

    return routeIdListObj
  }

  return null
}

function _generateRouteIdListFromCoordinates ({ from, to, coordinates, mapbox, routerId }) {
  if (coordinates) {
    let routeIdListObj = { geometryHash: [], routerId }

    if (mapbox) {
      routeIdListObj = { mapbox: true, geometryHash: [] }
    }

    routeIdListObj = { ...routeIdListObj, from, to }

    if (coordinates.length) {
      routeIdListObj.geometryHash = md5(_createUniqueIdentifierFromCoordinates(coordinates))
    } else {
      routeIdListObj.geometryHash = ''
    }

    return stableStringify(routeIdListObj)
  }

  return null
}

function _generateMapboxRouteIdList (route, options) {
  let routeIdListObj = { mapbox: true, geometryHash: [] }
  let coordinates

  if (route.legGeometry) {
    coordinates = _convertPolylineToCoordinates(route.legGeometry)
  } else {
    coordinates = route.geometry.coordinates
    coordinates = coordinates.map(o => ([o[1], o[0]]))
  }

  const from = options.from ? { lon: options.from[0], lat: options.from[1] } : undefined
  const to = options.to ? { lon: options.to[0], lat: options.to[1] } : undefined

  routeIdListObj.geometryHash = md5(_createUniqueIdentifierFromCoordinates(coordinates))
  routeIdListObj.from = from || { lon: route.geometry.coordinates[0][0], lat: route.geometry.coordinates[0][1] }
  const lastCoord = route.geometry.coordinates[route.geometry.coordinates.length - 1]
  routeIdListObj.to = to || { lon: lastCoord[0], lat: lastCoord[1] }

  return stableStringify(routeIdListObj)
}

/**
 * Get the start and destination labels of route legs
 * @param {array|object} route - the 'legs' property from an OTP route results object. Usually found in: plan -> itineraries[x]  -> legs
 * @returns {string}
 * @private
 */
function _generateRouteIdListStartDestinationLabel (route) {
  if (!_.isArray(route) && _.isObject(route) && route && route.geometry && route.geometry.coordinates) {
    const { origin, destination } = _generateMapboxRouteIdListStartDestinationLabel(route)
    return origin && destination ? `${origin} ${ROUTE_LABEL_CONNECTOR} ${destination}` : ''
  } else {
    const origin = _.find(route, o => (o.mode === 'BUS'))
    const destination = _.findLast(route, o => (o.mode === 'BUS'))
    return origin && destination ? `${origin.from.name} ${ROUTE_LABEL_CONNECTOR} ${destination.to.name}` : ''
  }
}

const ROUTE_LABEL_CONNECTOR = '↔'

function _generateMapboxRouteIdListStartDestinationLabel (route) {
  let origin, destination, startStep, endStep

  if (route.legs.length === 1) {
    const leg = route.legs[0]
    startStep = leg.steps[0]
    endStep = leg.steps[leg.steps.length - 1]
    if (!startStep.name && leg.summary) {
      startStep.name = leg.summary.split(',')[0].trim()
    }
    if (!endStep.name && leg.summary) {
      const summaries = leg.summary.split(',')
      if (summaries.length > 1) {
        endStep.name = summaries[1].trim()
      }
    }
  } else if (route.legs.length > 1) {
    const leg = route.legs[0]
    startStep = leg.steps[0]
    const legEnd = route.legs[route.legs.length - 1]
    endStep = legEnd.steps[legEnd.steps.length - 1]
    if (!startStep.name && leg.summary) {
      startStep.name = leg.summary.split(',')[0].trim()
    }
    if (!endStep.name && legEnd.summary) {
      const summaries = legEnd.summary.split(',')
      if (summaries.length > 1) {
        endStep.name = summaries[1].trim()
      }
    }
  }
  if (startStep && endStep) {
    origin = startStep.name
    destination = endStep.name
  }
  return { origin, destination }
}

/**
 * return radius distance between start and end coordinates
 * @param {*} startLngLat=undefined - {Point|{lng, lat}|[lng, lat]}
 * @param {*} endLngLat=undefined - {Point|{lng, lat}|[lng, lat]}
 * @param {string} units='kilometers'
 * @returns {number}
 * @public
 */
function _getRadiusOfBounds (startLngLat, endLngLat, units) {
  if (!startLngLat || (_.isArray(startLngLat) && !startLngLat.length) || !endLngLat ||
    (_.isArray(endLngLat) && !endLngLat.length)) {
    throw new Error('startLngLat and endLngLat params required')
  }
  const parsedUnit = units || 'kilometers'

  let totalDistance = turfDistance(startLngLat, endLngLat, { unit: parsedUnit })
  totalDistance = totalDistance / 2
  totalDistance = _.round(totalDistance)

  return totalDistance
}

let routeLineStringCache = {}

function _getFromRouteLineStringCache (routeId, polylineString) {
  if (routeLineStringCache[routeId]) {
    routeLineStringCache[routeId].timestamp = Date.now()
    return routeLineStringCache[routeId].routeLineString
  }
  if (Object.keys(routeLineStringCache).length > 10) {
    const lineStringArray = _.orderBy(_.map(routeLineStringCache, (o, key) => ({
      timestamp: o.timestamp,
      routeId: key
    })), ['timestamp'], ['desc'])
    delete routeLineStringCache[lineStringArray[0].routeId]
  }
  const routeLineString = turfHelpers.lineString(_convertPolylineToCoordinates(polylineString))
  routeLineStringCache[routeId] = { timestamp: Date.now(), routeLineString }
  return routeLineString
}

/**
 * Get amount charged on route (by fare zones) based on where the user started and where they currently are on the route
 * @param {object} zonesObject - An object of route ID keys to value (prices) used to determine prices to deduct from route fare based on zone the longlat is in {routeId: {fare: <price>, zone: <string>}, ..., identical: <price>}, identical means the routes are in the same zone
 * @param {array} itineraryLegs - Array of "leg" objects from a route itinerary object
 * @param {object|array} startCoordinates - {Point|{lng, lat}|[lng, lat]} where trip started (sometimes may not be at beginning of route)
 * * @param {object|array} currentCoordinates - {Point|{lng, lat}|[lng, lat]}
 * @returns {number|null} - if null returned, no "fare amount to be charged so far" could be determined with the data
 * @private
 */
function _prorateFareByZones (zonesObject, itineraryLegs, startCoordinates, currentCoordinates) {
  let proratedFare = null
  let startRouteId, endRouteId
  // find the route id that longLat is in
  for (let i = 0; i < itineraryLegs.length; i++) {
    if (itineraryLegs[i].routeId) {
      const routeLineString = _getFromRouteLineStringCache(itineraryLegs[i].routeId, itineraryLegs[i].legGeometry.points)
      if (routeLineString && _isOnRoute(routeLineString, startCoordinates) && zonesObject[itineraryLegs[i].routeId]) {
        startRouteId = itineraryLegs[i].routeId
        break
      }
    }
  }
  for (let i = 0; i < itineraryLegs.length; i++) {
    if (itineraryLegs[i].routeId) {
      const routeLineString = _getFromRouteLineStringCache(itineraryLegs[i].routeId, itineraryLegs[i].legGeometry.points)
      if (routeLineString && _isOnRoute(routeLineString, currentCoordinates) && zonesObject[itineraryLegs[i].routeId]) {
        endRouteId = itineraryLegs[i].routeId
        break
      }
    }
  }
  if (startRouteId && endRouteId) {
    const startZoneObject = zonesObject[startRouteId]
    const endZoneObject = zonesObject[endRouteId]
    if (startZoneObject.zone === endZoneObject.zone) {
      proratedFare = zonesObject.identical
    } else {
      proratedFare = endZoneObject.fare
    }
  }
  return proratedFare
}

/**
 * Get amount charged on route (by simple distance) based on where the user started and where they currently are on the route
 * @param {number} fare - Integer number (cents) of cost for route
 * @param {object} routeLineString - GeoJSON object containing coordinates of route line
 * @param {object|array} startCoordinates - {Point|{lng, lat}|[lng, lat]} where trip started (sometimes may not be at beginning of route)
 * @param {object|array} currentCoordinates - {Point|{lng, lat}|[lng, lat]}
 * @param {array} permutations=undefined - {lineLength (kilometers), fare} if available, check the price based on the distance current travelled
 * @param {boolean} perMeter=undefined - decide to charge per meter or by first x meters free
 * @param {number} minFreeLength = 0.1 - (kilometers) if distance travelled is <= minFreeLength, then don't charge a fare
 * @returns {number|null} - if null returned, no "fare amount to be charged so far" could be determined with the data
 * @private
 */
function _prorateFare (fare, routeLineString, startCoordinates, currentCoordinates, permutations = undefined, perMeter = undefined, minFreeLength = 0.1) {
  let proratedFare = null

  const currentLength = _distanceTravelled(routeLineString, currentCoordinates, startCoordinates)

  if (currentLength) {
    if (perMeter) {
      if (currentLength > minFreeLength) {
        const routeLength = turfLength(routeLineString)
        const farePerUnit = _.round(fare / routeLength, 5)
        proratedFare = Math.ceil(farePerUnit * currentLength)
        proratedFare = proratedFare > fare ? fare : proratedFare
      } else {
        proratedFare = 0
      }
    } else {
      if (currentLength > minFreeLength) {
        if (permutations && permutations.length) {
          const orderedPermutations = _.orderBy(permutations, ['lineLength'], ['asc'])
          for (i = 0; i < orderedPermutations.length; i++) {
            if (currentLength <= orderedPermutations.lineLength) {
              fare = orderedPermutations.fare
              break
            }
          }
        }
        proratedFare = fare
      } else {
        proratedFare = 0
      }
    }
  }

  return proratedFare
}

const OSM_METERS_PER_PIXEL_AT_EQUATOR = 156543.03

/**
 * Give a rough estimate (based on Open Street Map tile resolution of 512x512) of meters per pixel: https://wiki.openstreetmap.org/wiki/Slippy_map_tilenames#Resolution_and_Scale
 * @param {number} pixels
 * @param {number} latitude
 * @param {number} zoom
 * @returns {number}
 * @private
 */
function _approximateMetersFromPixels (pixels, latitude, zoom) {
  return pixels * ((OSM_METERS_PER_PIXEL_AT_EQUATOR * Math.cos(latitude)) / Math.pow(2, zoom))
}

/**
 * Accepts either a bare [lng, lat] pair or { coordinates: [lng, lat], timestamp }.
 * Timestamp may be epoch-ms or an ISO string. Missing/invalid times become null.
 * @param {array|object} sample
 * @returns {{coordinates: number[], timestamp: (number|null)}|null}
 */
function normalizeLocationSample (sample) {
  if (!sample) {
    return null
  }

  let coordinates = null
  let timestamp = null

  if (Array.isArray(sample)) {
    coordinates = sample
  } else if (typeof sample === 'object' && Array.isArray(sample.coordinates)) {
    coordinates = sample.coordinates
    if (sample.timestamp !== undefined && sample.timestamp !== null && sample.timestamp !== '') {
      const parsed = typeof sample.timestamp === 'number'
        ? sample.timestamp
        : new Date(sample.timestamp).getTime()
      if (!isNaN(parsed)) {
        timestamp = parsed
      }
    }
  }

  if (!coordinates || coordinates.length < 2) {
    return null
  }

  const lng = Number(coordinates[0])
  const lat = Number(coordinates[1])
  if (isNaN(lng) || isNaN(lat)) {
    return null
  }

  return { coordinates: [lng, lat], timestamp: timestamp }
}

/**
 * @param {array} samples
 * @returns {{coordinates: number[], timestamp: (number|null)}[]}
 */
function normalizeLocationSampleSet (samples) {
  const normalized = []
  if (!samples || !samples.length) {
    return normalized
  }

  for (let i = 0; i < samples.length; i++) {
    const sample = normalizeLocationSample(samples[i])
    if (sample) {
      normalized.push(sample)
    }
  }

  return normalized
}

/**
 * Time-span and gap guards apply only when every sample carries a timestamp.
 * Untimestamped arrays (legacy callers) skip these checks.
 * @param {{timestamp: (number|null)}[]} samples
 * @param {number} minTimeSpanSeconds
 * @param {number} maxSampleGapSeconds
 * @returns {boolean}
 */
function sampleTimeWindowIsUsable (samples, minTimeSpanSeconds, maxSampleGapSeconds) {
  if (!samples || samples.length < 2) {
    return true
  }

  for (let i = 0; i < samples.length; i++) {
    if (samples[i].timestamp === null || samples[i].timestamp === undefined) {
      return true
    }
  }

  const spanSeconds = (samples[samples.length - 1].timestamp - samples[0].timestamp) / 1000
  if (spanSeconds < minTimeSpanSeconds) {
    return false
  }

  for (let j = 1; j < samples.length; j++) {
    const gapSeconds = (samples[j].timestamp - samples[j - 1].timestamp) / 1000
    if (gapSeconds > maxSampleGapSeconds) {
      return false
    }
  }

  return true
}

/**
 * @param {object} routeLine - GeoJSON LineString
 * @param {number[]} coordinates
 * @returns {number}
 */
function distanceFromRouteLineKm (routeLine, coordinates) {
  try {
    return turfPointToLineDistance(turfHelpers.point(coordinates), routeLine, { units: 'kilometers' })
  } catch (err) {
    return Infinity
  }
}

/**
 * km/h between two timestamped samples. null when either timestamp is missing.
 * Zero or negative elapsed time is treated as an infinite jump so the later sample is dropped.
 * @param {{coordinates: number[], timestamp: (number|null)}} fromSample
 * @param {{coordinates: number[], timestamp: (number|null)}} toSample
 * @returns {number|null}
 */
function impliedSpeedKmPerHour (fromSample, toSample) {
  if (!fromSample || !toSample) {
    return null
  }
  if (fromSample.timestamp === null || fromSample.timestamp === undefined ||
      toSample.timestamp === null || toSample.timestamp === undefined) {
    return null
  }

  const elapsedMs = toSample.timestamp - fromSample.timestamp
  if (elapsedMs <= 0) {
    return Infinity
  }

  const km = turfDistance(fromSample.coordinates, toSample.coordinates, { units: 'kilometers' })
  return km / (elapsedMs / 3600000)
}

/**
 * Drops off-route points and physically impossible jumps. Remaining samples stay in order.
 * @param {object} options
 * @param {{coordinates: number[], timestamp: (number|null)}[]} options.samples
 * @param {object} options.routeLine
 * @param {number} options.maxSpeedKmPerHour
 * @param {number} options.maxDistanceFromRouteKm
 * @returns {{coordinates: number[], timestamp: (number|null)}[]}
 */
function filterDirectionSamples ({ samples, routeLine, maxSpeedKmPerHour, maxDistanceFromRouteKm }) {
  const accepted = []
  if (!samples || !samples.length) {
    return accepted
  }

  for (let i = 0; i < samples.length; i++) {
    const sample = samples[i]
    const offRouteDistance = distanceFromRouteLineKm(routeLine, sample.coordinates)
    if (offRouteDistance > maxDistanceFromRouteKm) {
      continue
    }

    if (accepted.length) {
      const speed = impliedSpeedKmPerHour(accepted[accepted.length - 1], sample)
      if (speed !== null && speed > maxSpeedKmPerHour) {
        continue
      }
    }

    accepted.push(sample)
  }

  return accepted
}

/**
 * Weighted-direction vote from along-route distances. Shared by the client picker and the
 * server-side travel-direction corrector.
 * @param {number[]} distances
 * @param {object} config
 * @returns {number} 1, -1, or 0
 */
function scoreTravelDirectionFromDistances (distances, config) {
  const minDistanceThreshold = config.minDistanceThreshold
  const confidenceThreshold = config.confidenceThreshold
  const stationaryThreshold = config.stationaryThreshold
  const useSmoothing = config.useSmoothing

  let scoredDistances = distances
  if (useSmoothing && scoredDistances.length >= 3) {
    scoredDistances = applyMovingAverageSmoothing(scoredDistances, 3)
  }

  const directionChanges = []
  let significantMovements = 0

  for (let i = 1; i < scoredDistances.length; i++) {
    const distanceChange = scoredDistances[i] - scoredDistances[i - 1]
    if (Math.abs(distanceChange) >= minDistanceThreshold) {
      significantMovements++
      if (distanceChange > 0) {
        directionChanges.push(1)
      } else {
        directionChanges.push(-1)
      }
    }
  }

  if (significantMovements === 0) {
    return 0
  }

  let weightedSum = 0
  let totalWeight = 0

  directionChanges.forEach(function (direction, index) {
    const weight = index + 1
    weightedSum += direction * weight
    totalWeight += weight
  })

  const weightedDirection = weightedSum / totalWeight
  const positiveChanges = directionChanges.filter(function (d) { return d > 0 }).length
  const negativeChanges = directionChanges.filter(function (d) { return d < 0 }).length
  const totalChanges = directionChanges.length
  const confidence = Math.max(positiveChanges, negativeChanges) / totalChanges

  if (confidence < confidenceThreshold) {
    return 0
  }

  const totalDistanceMoved = Math.abs(scoredDistances[scoredDistances.length - 1] - scoredDistances[0])
  if (totalDistanceMoved < stationaryThreshold) {
    return 0
  }

  if (weightedDirection > 0.3) {
    return 1
  } else if (weightedDirection < -0.3) {
    return -1
  }
  return 0
}

/**
 * Determines if a vehicle is moving towards or away from the destination based on location samples.
 * Samples may be [lng, lat] or { coordinates: [lng, lat], timestamp }.
 * @param {object} options
 * @param {array|object} options.routeLineCoordinates - Route line coordinates or LineString
 * @param {array|object} options.originCoordinate - Origin coordinate point
 * @param {array} options.currentLocationSampleSet - Location samples
 * @param {object} [options.config={}] - Configuration options
 * @param {number} [options.config.minDistanceThreshold=0.01] - Minimum along-route change (km) to count
 * @param {number} [options.config.minSampleSize=8] - Minimum raw samples required
 * @param {number} [options.config.confidenceThreshold=0.6] - Confidence threshold (0-1)
 * @param {boolean} [options.config.useSmoothing=true] - Smooth along-route distances
 * @param {number} [options.config.stationaryThreshold=0.02] - Overall movement below this is stationary (km)
 * @param {number} [options.config.maxSpeedKmPerHour=120] - Drop samples whose implied speed exceeds this
 * @param {number} [options.config.maxDistanceFromRouteKm=0.5] - Drop samples farther than this from the line
 * @param {number} [options.config.minTimeSpanSeconds=45] - Require this span when timestamps are present
 * @param {number} [options.config.maxSampleGapSeconds=30] - Reject windows with a larger internal gap
 * @returns {number} 1 (towards destination), -1 (away from destination), 0 (unclear/stationary)
 */
function isMovingToDestination ({
  routeLineCoordinates,
  originCoordinate,
  currentLocationSampleSet,
  config = {}
}) {
  const minDistanceThreshold = config.minDistanceThreshold !== undefined ? config.minDistanceThreshold : 0.01
  const minSampleSize = config.minSampleSize !== undefined ? config.minSampleSize : 8
  const confidenceThreshold = config.confidenceThreshold !== undefined ? config.confidenceThreshold : 0.6
  const useSmoothing = config.useSmoothing !== undefined ? config.useSmoothing : true
  const stationaryThreshold = config.stationaryThreshold !== undefined ? config.stationaryThreshold : 0.02
  const maxSpeedKmPerHour = config.maxSpeedKmPerHour !== undefined ? config.maxSpeedKmPerHour : 120
  const maxDistanceFromRouteKm = config.maxDistanceFromRouteKm !== undefined ? config.maxDistanceFromRouteKm : 0.5
  const minTimeSpanSeconds = config.minTimeSpanSeconds !== undefined ? config.minTimeSpanSeconds : 45
  const maxSampleGapSeconds = config.maxSampleGapSeconds !== undefined ? config.maxSampleGapSeconds : 30

  if (!currentLocationSampleSet || currentLocationSampleSet.length < minSampleSize) {
    return 0
  }

  if (!routeLineCoordinates) {
    return 0
  }

  if (Array.isArray(routeLineCoordinates) && routeLineCoordinates.length < 2) {
    return 0
  }

  let routeLine = routeLineCoordinates
  if (Array.isArray(routeLineCoordinates)) {
    routeLine = turfHelpers.lineString(routeLineCoordinates)
  }

  const normalized = normalizeLocationSampleSet(currentLocationSampleSet)
  if (normalized.length < minSampleSize) {
    return 0
  }

  if (!sampleTimeWindowIsUsable(normalized, minTimeSpanSeconds, maxSampleGapSeconds)) {
    return 0
  }

  const filteredSamples = filterDirectionSamples({
    samples: normalized,
    routeLine: routeLine,
    maxSpeedKmPerHour: maxSpeedKmPerHour,
    maxDistanceFromRouteKm: maxDistanceFromRouteKm
  })

  if (filteredSamples.length < 3) {
    return 0
  }

  const distances = []
  for (let i = 0; i < filteredSamples.length; i++) {
    const sample = filteredSamples[i]
    distances.push(_distanceTravelled(
      routeLine,
      turfHelpers.point(sample.coordinates),
      originCoordinate
    ))
  }

  return scoreTravelDirectionFromDistances(distances, {
    minDistanceThreshold: minDistanceThreshold,
    confidenceThreshold: confidenceThreshold,
    stationaryThreshold: stationaryThreshold,
    useSmoothing: useSmoothing
  })
}

/**
 * Apply moving average smoothing to reduce GPS noise
 * @param {number[]} values - Array of distance values
 * @param {number} windowSize - Size of the moving average window
 * @returns {number[]} Smoothed values
 */
function applyMovingAverageSmoothing (values, windowSize = 3) {
  const smoothed = []
  
  for (let i = 0; i < values.length; i++) {
    const start = Math.max(0, i - Math.floor(windowSize / 2))
    const end = Math.min(values.length, start + windowSize)
    const window = values.slice(start, end)
    const average = window.reduce((sum, val) => sum + val, 0) / window.length
    smoothed.push(average)
  }
  
  return smoothed
}

/**
 * Given a coordinate (longLat) check if it is ahead (closer to the end of a line) than another coordinate (otherLongLat)
 * @param {object} options
 * @param {object|array} options.longLat
 * @param {{type:string,properties:object,geometry:{type:string,coordinates:array[]}}|array} options.otherLongLat
 * @param {object|array[]} options.lineString
 * @returns {boolean}
 * @private
 */
function isCoordinateAheadOnLine ({ longLat, otherLongLat, lineString }) {
  const currentLongLatPoint = Array.isArray(longLat) ? turfHelpers.point(longLat) : longLat
  const otherCurrentLongLatPoint = Array.isArray(otherLongLat) ? turfHelpers.point(otherLongLat) : otherLongLat
  const currentLineString = Array.isArray(lineString) ? turfHelpers.lineString(lineString) : lineString
  const endOfLinePoint = turfHelpers.point(currentLineString.geometry.coordinates[currentLineString.geometry.coordinates.length - 1])

  const firstSlicedLine = turfLineSlice(currentLongLatPoint, endOfLinePoint, currentLineString)
  const firstSlicedLineDistance = turfLength(firstSlicedLine)
  const secondSlicedLine = turfLineSlice(otherCurrentLongLatPoint, endOfLinePoint, currentLineString)
  const secondSlicedLineDistance = turfLength(secondSlicedLine)

  return firstSlicedLineDistance < secondSlicedLineDistance
}

/**
 * @param {object} options
 * @param {object|array} options.currentLongLat
 * @param {{longlat:array, id:number}[]} options.busStops
 * @param {object|array[]} options.lineString
 * @param {number} [options.minDistanceKm]
 * @returns {{excludedBusStops: *[], nextBusStop: (null|{longlat:array, id:number, externalName:string})}}
 * @private
 */
function getNextBusStopOnRoute ({ currentLongLat, busStops, lineString, minDistanceKm }) {
  const excludedBusStops = []
  const currentLongLatPoint = Array.isArray(currentLongLat) ? turfHelpers.point(currentLongLat) : currentLongLat
  const currentLineString = Array.isArray(lineString) ? turfHelpers.lineString(lineString) : lineString

  let busStop, distanceToNextStop

  if (_isOnRoute(currentLineString, currentLongLatPoint)) {
    for (let i = 0; i < busStops.length; i++) {
      busStop = busStops[i]
      const busStopPoint = turfHelpers.point(busStop.longlat)
      const isCurrentPointAheadOfBusStopPoint = isCoordinateAheadOnLine({
        longLat: currentLongLatPoint,
        otherLongLat: busStopPoint,
        lineString: currentLineString
      })

      if (isCurrentPointAheadOfBusStopPoint) {
        excludedBusStops.push(busStop)
        busStop = null
        continue
      }

      const slicedLine = turfLineSlice(currentLongLatPoint, busStopPoint, currentLineString)
      const slicedLineDistance = slicedLine ? turfLength(slicedLine) : null

      if (minDistanceKm) {
        if (slicedLineDistance > minDistanceKm) {
          continue
        }
      }

      if (slicedLineDistance > 0.01) {
        distanceToNextStop = slicedLineDistance
        break
      } else {
        excludedBusStops.push(busStop)
        busStop = null
      }
    }
  }

  return {
    distanceToNextStop,
    excludedBusStops,
    nextBusStop: busStop
  }
}

const MIN_DISTANCE_ARRIVAL_DETECTION = 0.09

function getCurrentBusStopOnRoute ({ currentLongLat, busStops, lineString }) {
  let busStop
  let slicedLineDistance
  const excludedBusStops = []
  const currentLongLatPoint = Array.isArray(currentLongLat) ? turfHelpers.point(currentLongLat) : currentLongLat
  const currentLineString = Array.isArray(lineString) ? turfHelpers.lineString(lineString) : lineString
  const minDistanceForArrivedKm = MIN_DISTANCE_ARRIVAL_DETECTION

  if (_isOnRoute(lineString, currentLongLatPoint)) {
    // for (let i = 0; i < busStops.length; i++) {
    //   busStop = busStops[i]
    //
    //   if (_isWithinRadius({
    //     lng: currentLongLatPoint.geometry.coordinates[0],
    //     lat: currentLongLatPoint.geometry.coordinates[1],
    //     distance: 0.01
    //   }, {
    //     lng: busStop.longlat[0],
    //     lat: busStop.longlat[1],
    //     distance: 0.02
    //   })) {
    //     break
    //   } else {
    //     busStop = null
    //   }
    // }
    for (let i = 0; i < busStops.length; i++) {
      busStop = busStops[i]
      const busStopPoint = turfHelpers.point(busStop.longlat)

      const isCurrentPointAheadOfBusStopPoint = isCoordinateAheadOnLine({
        longLat: currentLongLatPoint,
        otherLongLat: busStopPoint,
        lineString: currentLineString
      })

      if (isCurrentPointAheadOfBusStopPoint) {
        excludedBusStops.push(busStop)
        busStop = null
      } else {
        const slicedLine = turfLineSlice(currentLongLatPoint, busStopPoint, currentLineString)
        slicedLineDistance = slicedLine ? turfLength(slicedLine) : null

        if (slicedLineDistance <= minDistanceForArrivedKm) {
          break
        } else {
          excludedBusStops.push(busStop)
          busStop = null
        }
      }
    }
  }

  return { busStop, excludedBusStops, distanceToCurrentStop: slicedLineDistance }
}

/**
 * Track for upcoming busstop or current busstop
 * Usage eg.: const busStopTracker = new BusStopTracker({ callback: ({currentBusStop,nextBusStop,distanceToNextStop}) => {
 *   ...if currentBusStop is defined do something
 *   ...if nextBusStop is defined do something
 *   ... the above params may not be defined if they are not going to be encountered of if they are encountered and do not be alerted/repeated
 * }, ... })
 * Now to update the current position, call the function below:
 * busStopTracker.updateBusStopAssets({ lineString, busStops, currentLongLat })
 *
 * @param {object} options
 * @param {({currentBusStop,nextBusStop,distanceToNextStop}) => {}} options.callback
 * @param {object|array[]} options.lineString
 * @param {{longlat:array, id:number}[]} options.busStops
 * @param {object|number[]} options.currentLongLat
 * @constructor
 */
function BusStopTracker ({ callback, lineString, busStops, currentLongLat }) {
  /**
   * update assets if needed
   * @param {object} options
   * @param {object|array[]} [options.lineString]
   * @param {{longlat:array, id:number}[]} [options.busStops]
   * @param {object|number[]} options.currentLongLat
   * @param {number} [options.repeatNextStopAfterMs] - set this to re-publish the next stop name again (if identical to last published one) after X milliseconds have elapsed
   */
  this.updateBusStopAssets = function ({ lineString, busStops, currentLongLat, repeatNextStopAfterMs }) {
    if (Array.isArray(busStops)) {
      this._busStops = busStops
    }

    if (lineString) {
      this._currentLineString = Array.isArray(lineString) ? turfHelpers.lineString(lineString) : lineString
    }

    if (!currentLongLat) {
      return
    }

    this._currentLongLatPoint = Array.isArray(currentLongLat) ? turfHelpers.point(currentLongLat) : currentLongLat

    if (!this.initialUpdateCompleted) {
      this._busStops = this._busStops.filter((busStop) => {
        const onRoute = _isOnRoute(this._currentLineString, busStop.longlat, 0.02)

        if (!onRoute) {
          this._excludedRouteBusStops.push(busStop)
          this._excludedArrivedRouteBusStops.push(busStop)
        }

        return onRoute
      })
    }

    this._busStopsForArrived = [].concat(this._busStops)

    const { excludedBusStops: excludedArrivedBusStops, busStop: currentBusStop, distanceToCurrentStop } = getCurrentBusStopOnRoute({
      currentLongLat: this._currentLongLatPoint,
      busStops: this._busStopsForArrived,
      lineString: this._currentLineString
    })

    // console.warn('excludedArrivedBusStops', excludedArrivedBusStops, 'currentBusStop', currentBusStop)

    const { excludedBusStops, distanceToNextStop, nextBusStop } = getNextBusStopOnRoute({
      currentLongLat: this._currentLongLatPoint,
      busStops: this._busStops,
      lineString: this._currentLineString
    })

    this._excludedRouteBusStops = this._excludedRouteBusStops.concat(excludedBusStops)
    this._excludedArrivedRouteBusStops = this._excludedArrivedRouteBusStops.concat(excludedArrivedBusStops)
    this._busStops = this._busStops.filter((o) => (!this._excludedRouteBusStops.find((b) => b.id === o.id)))
    this._busStopsForArrived = this._busStopsForArrived.filter((o) => (!this._excludedArrivedRouteBusStops.find((b) => b.id === o.id)))

    if (currentBusStop || nextBusStop || distanceToNextStop) {
      let publishNextStop = false

      if (nextBusStop) {
        if (this._nextBusStop && this._nextBusStop.id === nextBusStop.id) {
          if (repeatNextStopAfterMs && this.lastTimestampNextBusStopAnnounced && (Date.now() - this.lastTimestampNextBusStopAnnounced) > repeatNextStopAfterMs) {
            publishNextStop = true
            this.lastTimestampNextBusStopAnnounced = Date.now()
          }
        } else {
          publishNextStop = true
          this.lastTimestampNextBusStopAnnounced = Date.now()
        }
      }

      callback({
        currentBusStop: (this._currentBusStop && this._currentBusStop.id) !== (currentBusStop && currentBusStop.id) ? currentBusStop : undefined,
        nextBusStop: publishNextStop ? nextBusStop : undefined,
        distanceToNextStop: distanceToNextStop || distanceToNextStop === 0 ? _.round(distanceToNextStop, 3) : undefined,
        distanceToCurrentStop: distanceToCurrentStop || distanceToCurrentStop === 0 ? _.round(distanceToCurrentStop, 3) : undefined
      })
    }

    this._currentBusStop = currentBusStop
    this._nextBusStop = nextBusStop
  }

  this.lastTimestampNextBusStopAnnounced = null
  this._busStops = []
  this._busStopsForArrived = []
  this._currentLongLatPoint = null
  this._currentLineString = null
  this._nextBusStop = null
  this._currentBusStop = null
  this._excludedRouteBusStops = []
  this._excludedArrivedRouteBusStops = []

  if (currentLongLat || busStops || lineString) {
    this.updateBusStopAssets({ lineString, busStops, currentLongLat })
    this.initialUpdateCompleted = true
  }
}


module.exports = {
  approximateMetersFromPixels: _approximateMetersFromPixels,

  isWithinRadius: _isWithinRadius,

  convertPolylineToGeoJSON: _convertPolylineToGeoJSON,

  convertPolylineToCoordinates: _convertPolylineToCoordinates,

  convertCoordinatesToPolyline: _convertCoordinatesToPolyline,

  calculateTotalItineraryDistances: _calculateTotalItineraryDistances,

  metersToMiles: _metersToMiles,

  milesToMeters: _milesToMeters,

  PassengerOnTransportationChecker: _PassengerOnTransportationChecker,

  distanceOnRouteLine,

  distanceTravelled: _distanceTravelled,

  generateRouteLayer: _generateRouteLayer,

  getPointsWithinRadius: _getPointsWithinRadius,

  getPointsWithinPolygon: _getPointsWithinPolygon,

  getParsedBoundsArrayForPolygon,

  RouteLinePointChecker: _RouteLinePointChecker,

  RouteLineMover: _RouteLineMover,

  animateOnLine: _animateOnLine,

  isOnRoute: _isOnRoute,

  getRadiusOfBounds: _getRadiusOfBounds,

  generateRouteIdList: _generateRouteIdList,

  generateRouteIdListStartDestinationLabel: _generateRouteIdListStartDestinationLabel,

  prorateFareByZones: _prorateFareByZones,

  prorateFare: _prorateFare,

  sliceRoute: _sliceRoute,

  FilterGeolocation: _FilterGeolocation,

  getNextBusStopOnRoute,

  getCurrentBusStopOnRoute,

  isCoordinateAheadOnLine,

  isMovingToDestination,

  generateRouteIdListFromCoordinates: _generateRouteIdListFromCoordinates,

  BusStopTracker,

  ROUTE_LABEL_CONNECTOR
}

// eslint-disable-next-line
const _MD5 = function(d){result = M(V(Y(X(d),8*d.length)));return result.toLowerCase()};function M(d){for(var _,m="0123456789ABCDEF",f="",r=0;r<d.length;r++)_=d.charCodeAt(r),f+=m.charAt(_>>>4&15)+m.charAt(15&_);return f}function X(d){for(var _=Array(d.length>>2),m=0;m<_.length;m++)_[m]=0;for(m=0;m<8*d.length;m+=8)_[m>>5]|=(255&d.charCodeAt(m/8))<<m%32;return _}function V(d){for(var _="",m=0;m<32*d.length;m+=8)_+=String.fromCharCode(d[m>>5]>>>m%32&255);return _}function Y(d,_){d[_>>5]|=128<<_%32,d[14+(_+64>>>9<<4)]=_;for(var m=1732584193,f=-271733879,r=-1732584194,i=271733878,n=0;n<d.length;n+=16){var h=m,t=f,g=r,e=i;f=md5_ii(f=md5_ii(f=md5_ii(f=md5_ii(f=md5_hh(f=md5_hh(f=md5_hh(f=md5_hh(f=md5_gg(f=md5_gg(f=md5_gg(f=md5_gg(f=md5_ff(f=md5_ff(f=md5_ff(f=md5_ff(f,r=md5_ff(r,i=md5_ff(i,m=md5_ff(m,f,r,i,d[n+0],7,-680876936),f,r,d[n+1],12,-389564586),m,f,d[n+2],17,606105819),i,m,d[n+3],22,-1044525330),r=md5_ff(r,i=md5_ff(i,m=md5_ff(m,f,r,i,d[n+4],7,-176418897),f,r,d[n+5],12,1200080426),m,f,d[n+6],17,-1473231341),i,m,d[n+7],22,-45705983),r=md5_ff(r,i=md5_ff(i,m=md5_ff(m,f,r,i,d[n+8],7,1770035416),f,r,d[n+9],12,-1958414417),m,f,d[n+10],17,-42063),i,m,d[n+11],22,-1990404162),r=md5_ff(r,i=md5_ff(i,m=md5_ff(m,f,r,i,d[n+12],7,1804603682),f,r,d[n+13],12,-40341101),m,f,d[n+14],17,-1502002290),i,m,d[n+15],22,1236535329),r=md5_gg(r,i=md5_gg(i,m=md5_gg(m,f,r,i,d[n+1],5,-165796510),f,r,d[n+6],9,-1069501632),m,f,d[n+11],14,643717713),i,m,d[n+0],20,-373897302),r=md5_gg(r,i=md5_gg(i,m=md5_gg(m,f,r,i,d[n+5],5,-701558691),f,r,d[n+10],9,38016083),m,f,d[n+15],14,-660478335),i,m,d[n+4],20,-405537848),r=md5_gg(r,i=md5_gg(i,m=md5_gg(m,f,r,i,d[n+9],5,568446438),f,r,d[n+14],9,-1019803690),m,f,d[n+3],14,-187363961),i,m,d[n+8],20,1163531501),r=md5_gg(r,i=md5_gg(i,m=md5_gg(m,f,r,i,d[n+13],5,-1444681467),f,r,d[n+2],9,-51403784),m,f,d[n+7],14,1735328473),i,m,d[n+12],20,-1926607734),r=md5_hh(r,i=md5_hh(i,m=md5_hh(m,f,r,i,d[n+5],4,-378558),f,r,d[n+8],11,-2022574463),m,f,d[n+11],16,1839030562),i,m,d[n+14],23,-35309556),r=md5_hh(r,i=md5_hh(i,m=md5_hh(m,f,r,i,d[n+1],4,-1530992060),f,r,d[n+4],11,1272893353),m,f,d[n+7],16,-155497632),i,m,d[n+10],23,-1094730640),r=md5_hh(r,i=md5_hh(i,m=md5_hh(m,f,r,i,d[n+13],4,681279174),f,r,d[n+0],11,-358537222),m,f,d[n+3],16,-722521979),i,m,d[n+6],23,76029189),r=md5_hh(r,i=md5_hh(i,m=md5_hh(m,f,r,i,d[n+9],4,-640364487),f,r,d[n+12],11,-421815835),m,f,d[n+15],16,530742520),i,m,d[n+2],23,-995338651),r=md5_ii(r,i=md5_ii(i,m=md5_ii(m,f,r,i,d[n+0],6,-198630844),f,r,d[n+7],10,1126891415),m,f,d[n+14],15,-1416354905),i,m,d[n+5],21,-57434055),r=md5_ii(r,i=md5_ii(i,m=md5_ii(m,f,r,i,d[n+12],6,1700485571),f,r,d[n+3],10,-1894986606),m,f,d[n+10],15,-1051523),i,m,d[n+1],21,-2054922799),r=md5_ii(r,i=md5_ii(i,m=md5_ii(m,f,r,i,d[n+8],6,1873313359),f,r,d[n+15],10,-30611744),m,f,d[n+6],15,-1560198380),i,m,d[n+13],21,1309151649),r=md5_ii(r,i=md5_ii(i,m=md5_ii(m,f,r,i,d[n+4],6,-145523070),f,r,d[n+11],10,-1120210379),m,f,d[n+2],15,718787259),i,m,d[n+9],21,-343485551),m=safe_add(m,h),f=safe_add(f,t),r=safe_add(r,g),i=safe_add(i,e)}return Array(m,f,r,i)}function md5_cmn(d,_,m,f,r,i){return safe_add(bit_rol(safe_add(safe_add(_,d),safe_add(f,i)),r),m)}function md5_ff(d,_,m,f,r,i,n){return md5_cmn(_&m|~_&f,d,_,r,i,n)}function md5_gg(d,_,m,f,r,i,n){return md5_cmn(_&f|m&~f,d,_,r,i,n)}function md5_hh(d,_,m,f,r,i,n){return md5_cmn(_^m^f,d,_,r,i,n)}function md5_ii(d,_,m,f,r,i,n){return md5_cmn(m^(_|~f),d,_,r,i,n)}function safe_add(d,_){var m=(65535&d)+(65535&_);return(d>>16)+(_>>16)+(m>>16)<<16|65535&m}function bit_rol(d,_){return d<<_|d>>>32-_}

function md5 (value) {
  return _MD5(encodeURIComponent(value))
}

